import React, { useMemo, useState, useRef } from "react"
import {
  Container,
  Row,
  Col,
  CardBody,
  CardTitle,
} from "reactstrap"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import Slider from "react-slick"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"

// Import components
import ArticleCardCarousel from "../../additional_components/ArticleCard"
import TrackedGame from "../../additional_components/TrackedGame"
import OptimizedImage from "../../components/Common/OptimizedImage"
import FilterPanel from "../../components/KeywordTrackingNewUIPage/FilterPanel"
import PlatformTabs from "../../components/Common/PlatformTabs"
import KTRLineChart from "../../additional_components/common/charts-graphs/KTRLineChart"
import SEO from "../../components/Common/SEO"
import GuestRestrictedPlaceholder from "../../components/Common/GuestRestrictedPlaceholder"

// Import utilities
import { queryKeys } from "../../utils/queryClient"
import { apiService } from "../../utils/prefetchManager"
import { usePerformanceMonitor } from "../../hooks/usePerformanceMonitor"
import { PAGE_SEO, STRUCTURED_DATA } from "../../config/seoConfig"
import { generateCanonicalUrl, generateSlug, getAppDetailsLink } from "../../utils/seoUtils"
import { useFilters } from "../../hooks/useFilters"
import { usePlatforms } from "../../contexts/PlatformsContext"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"

// Optimized carousel settings - standardized for web, tablet, and mobile
// Optimized carousel settings - standardized for web, tablet, and mobile
// Optimized carousel settings - standardized for web, tablet, and mobile
const carouselSettings = {
  dots: false,
  infinite: true,
  speed: 500,
  slidesToShow: 10, // Default for largest screens (≥1600px)
  slidesToScroll: 1,
  autoplay: false,
  arrows: false,
  swipe: true,
  touchMove: true,
  centerMode: false,
  centerPadding: "0px",
  responsive: [
    {
      breakpoint: 1600, // Below 1600px
      settings: {
        slidesToShow: 9, // 9 cards for 1400-1599px
        slidesToScroll: 1,
        arrows: false,
        centerMode: false,
        centerPadding: "0px",
      },
    },
    {
      breakpoint: 1400, // Below 1400px
      settings: {
        slidesToShow: 8, // 8 cards for 1200-1399px
        slidesToScroll: 1,
        arrows: false,
        centerMode: false,
        centerPadding: "0px",
      },
    },
    {
      breakpoint: 1200, // Below 1200px (tablet)
      settings: {
        slidesToShow: 9,
        slidesToScroll: 1,
        arrows: false,
        centerMode: false,
        centerPadding: "0px",
      },
    },
    {
      breakpoint: 1024, // Below 1200px (tablet)
      settings: {
        slidesToShow: 8,
        slidesToScroll: 1,
        arrows: false,
        centerMode: false,
        centerPadding: "0px",
      },
    },
    {
      breakpoint: 840, // Mobile
      settings: {
        slidesToShow: 7,
        slidesToScroll: 1,
        arrows: false,
        dots: true,
        centerMode: false,
        centerPadding: "0px",
      },
    },
    {
      breakpoint: 768, // Mobile
      settings: {
        slidesToShow: 3,
        slidesToScroll: 1,
        arrows: false,
        dots: true,
        centerMode: false,
        centerPadding: "0px",
      },
    },
    {
      breakpoint: 676,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
        arrows: false,
        dots: true,
        centerMode: false,
        centerPadding: "0px",
      },
    },
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        dots: true,
        centerMode: false,
        centerPadding: "0px",
      },
    },
  ],
}

// Memoized Carousel Component
const CarouselComponent = React.memo(
  ({
    games,
    title,
    defaultOpenGraph,
    disableLink = false,
    onCardClick,
    selectedAppId,
    slidesToShowOverride,
    cardDimsOverride,
  }) => {
    const { getPlatformIcon } = usePlatforms();
    
    // Get window width for responsive card sizing
    const [windowWidth, setWindowWidth] = React.useState(
      typeof window !== "undefined" ? window.innerWidth : 0,
    )

    React.useEffect(() => {
      const handleResize = () => {
        setWindowWidth(window.innerWidth)
      }
      window.addEventListener("resize", handleResize)
      return () => window.removeEventListener("resize", handleResize)
    }, [])

    // Responsive card dimensions
    const getCardDimensions = () => {
      // Allow per-instance override of card dimensions (useful to restore
      // the original mobile visuals for specific carousels like Top Performer)
      if (cardDimsOverride) {
        if (windowWidth < 768 && cardDimsOverride.mobile) return cardDimsOverride.mobile
        if (windowWidth < 1200 && cardDimsOverride.tablet) return cardDimsOverride.tablet
        if (cardDimsOverride.desktop) return cardDimsOverride.desktop
      }

      if (windowWidth < 768) {
        // Mobile - bigger cards, show 1 at a time
        return {
          maxWidth: "140px",
          minWidth: "120px",
          height: "220px",
          imageHeight: "120px",
          imageWidth: "120px",
        }
      } else if (windowWidth < 1200) {
        // Tablet - bigger cards for web/tablet view (10 cards per page)
        return {
          maxWidth: "70px",
          minWidth: "64px",
          height: "84px",
          imageHeight: "36px",
          imageWidth: "36px",
        }
      } else {
        // Desktop - bigger cards for web/tablet view (10 cards per page)
        return {
          maxWidth: "100%",
          minWidth: "100%",
          height: "80px",
          imageHeight: "32px",
          imageWidth: "32px",
        }
      }
    }

    const cardDims = getCardDimensions()

    if (!games || games.length === 0) {
      return (
        <div className="d-flex gap-3">
          {Array.from({ length: 5 }).map((_, idx) => (
            <div key={idx} style={{ minWidth: cardDims.minWidth }}>
              <Skeleton
                height={cardDims.height}
                borderRadius={8}
                baseColor="#171821"
                highlightColor="#2a2b37"
                enableAnimation={true}
              />
              <Skeleton
                width="70%"
                height={16}
                style={{ marginTop: "12px" }}
                baseColor="#171821"
                highlightColor="#2a2b37"
                enableAnimation={true}
              />
            </div>
          ))}
        </div>
      )
    }

    // Adjust settings per-instance so we don't show duplicate slides when the
    // number of items is less than the configured `slidesToShow` (react-slick
    // will clone slides when `infinite: true` and there are fewer slides than
    // the visible area, causing visible duplicates). We compute a local
    // settings object that reduces slidesToShow and disables infinite when
    // needed.
    const baseSlidesToShow = slidesToShowOverride || carouselSettings.slidesToShow || 6
    const desiredSlidesToShow = baseSlidesToShow
    const slidesToShow = Math.max(
      1,
      Math.min(desiredSlidesToShow, games.length || 1),
    )

    const localSettings = {
      ...carouselSettings,
      // slidesToShow,
      // responsive: updatedResponsive,
      // Only allow infinite if there are more items than visible slots
      infinite: (games.length || 0) > slidesToShow,
    }

    // Calculate responsive gap between cards
    const getCardGap = () => {
      if (windowWidth < 768) return 12 // Mobile - keep original
      if (windowWidth < 1200) return 0.5 // Tablet - 0.5px gap
      // Desktop - scale with screen size (0.5px to 1.5px)
      return Math.min(1.5, 0.5 + (windowWidth - 1200) / 1000)
    }

    const cardGap = getCardGap()
    const cardGapHalf = cardGap / 2 // Half gap for padding (each side)

    return (
      <Slider {...localSettings} dots={false}>
        {games.map((app, index) => {
          const isWebOrTablet = windowWidth >= 768
          const isMobile = windowWidth < 768
          const cardMarkup = (
            <div
              // className="highlight-card"
              className=""
              style={{
                position: "relative",
                width: "100%",
                maxWidth: cardDims.maxWidth,
                minWidth: cardDims.minWidth,
                height: cardDims.height,
                background: "#171821",
                border: "1px solid #353748",
                borderRadius: "8px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                margin: "auto",
                cursor: "pointer",
                alignItems: isMobile || isWebOrTablet ? "center" : "stretch",
                padding: isMobile || isWebOrTablet ? "8px" : "0",
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: cardDims.imageWidth ? cardDims.imageWidth : (isMobile ? cardDims.imageHeight : "100%"),
                  height: cardDims.imageHeight ? cardDims.imageHeight : (isMobile ? cardDims.imageHeight : "70%"),
                  overflow: "hidden",
                  borderRadius: isMobile || isWebOrTablet ? "6px" : "8px 8px 0 0",
                  flexShrink: 0,
                  alignSelf: isMobile || isWebOrTablet ? "center" : "stretch",
                }}
              >
                <OptimizedImage
                  src={
                    app.image_url ||
                    "https://via.placeholder.com/253x250/171821/ffffff?text=Loading..."
                  }
                  alt={`${app.name} Game Image`}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center",
                    borderRadius: isMobile || isWebOrTablet ? "6px" : "8px 8px 0 0",
                  }}
                  lazy={true}
                  quality={85}
                />
              </div>

              {/* Platform Icon Badge - positioned at card level */}
              {app.platformId && getPlatformIcon(app.platformId) && (
                <img
                  src={getPlatformIcon(app.platformId) || ""}
                  alt={app.platform || app.platformId}
                  style={{
                    position: "absolute",
                    top: isMobile || isWebOrTablet ? 4 : 4,
                    right: isMobile || isWebOrTablet ? 4 : 4,
                    width: isMobile ? 28 : 24,
                    height: isMobile ? 28 : 24,
                    objectFit: "contain",
                    zIndex: 10,
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}

              <CardBody
                style={{
                  backgroundColor: isMobile || isWebOrTablet ? "transparent" : "#1e1e28",
                  padding: isMobile || isWebOrTablet ? "4px 0px" : "0px 5px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  width: "100%",
                }}
              >
                <CardTitle
                  tag="h3"
                  className="mb-0 text-center trending_game_titleup"
                  style={{
                    fontSize: isMobile ? "14px" : (isWebOrTablet ? "10px" : "12px"),
                    lineHeight: "1.3",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    fontWeight: "normal",
                    WebkitLineClamp: isMobile ? 3 : (isWebOrTablet ? 1 : 2),
                    WebkitBoxOrient: "vertical",
                    wordBreak: "break-word",
                    maxHeight: "100%",
                    margin: 0,
                    width: "100%",
                  }}
                >
                  {app.name}
                </CardTitle>
              </CardBody>
            </div>
          )

          return (
            <div
              key={app.item_id || `${index}`}
              style={{
                padding: windowWidth < 768 ? "0 12px" : `0 ${cardGapHalf}px`,
                boxSizing: "border-box",
              }}
            >
              {disableLink ? (
                <div
                  className={`card-link-wrapper ${selectedAppId === app.item_id ? "selected-app" : ""}`}
                  onClick={() => onCardClick && onCardClick(app)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={e => {
                    if (e.key === "Enter" || e.key === " ")
                      onCardClick && onCardClick(app)
                  }}
                >
                  {cardMarkup}
                </div>
              ) : (
                <Link
                  to={getAppDetailsLink(app)}
                  className="card-link-wrapper"
                  state={{
                    app_id: app.item_id,
                    data: app,
                    openGraph: defaultOpenGraph,
                  }}
                >
                  {cardMarkup}
                </Link>
              )}
            </div>
          )
        })}
      </Slider>
    )
  },
)

CarouselComponent.displayName = "CarouselComponent"

// Helper function to generate page numbers with ellipsis for pagination
const getPageNumbers = (currentPage, totalPages) => {
  const pages = []

  // Always show pages 1, 2, 3
  for (let i = 1; i <= Math.min(3, totalPages); i++) {
    pages.push(i)
  }

  // If there are more than 3 pages, show ellipsis
  if (totalPages > 3) {
    // If current page is beyond 3, show current page after ellipsis
    if (currentPage > 3) {
      pages.push("...")
      pages.push(currentPage)
    } else {
      // Just show ellipsis
      pages.push("...")
    }
  }

  return pages
}

// Builds a compact JSON string of the non-empty applied filters for the
// `dashboard_filter_applied` GA4 event - empty/blank keys are dropped and the
// platform key is excluded (it's tracked separately via `dashboard_platform_toggled`),
// since GA4 string params should stay short.
const summarizeFilters = (sel) => {
  if (!sel || typeof sel !== "object") return "{}"
  const compact = {}
  Object.entries(sel).forEach(([key, value]) => {
    if (key === "platform") return
    if (value == null) return
    if (Array.isArray(value) && value.length === 0) return
    if (typeof value === "string" && value.trim() === "") return
    compact[key] = value
  })
  const str = JSON.stringify(compact)
  return str.length > 100 ? `${str.slice(0, 97)}...` : str
}

// Helper to reactively map platform info onto game objects.
// Runs in the render loop (via useMemo) so it always uses the latest
// platforms data, fixing the race condition where queryFn resolves
// before PlatformsContext finishes fetching from the API.
const mapPlatformInfoToGames = (games, getPlatformIcon, getPlatformDisplayName, activePlatforms, platformFilter) => {
  if (!Array.isArray(games) || games.length === 0) return games
  if (!activePlatforms || activePlatforms.length === 0) return games

  const extractPlatformId = (platformData) => {
    if (Array.isArray(platformData) && platformData.length > 0) return platformData[0]
    return platformData
  }

  return games.map(game => {
    // If game already has a platformId, ensure icon is up-to-date
    if (game.platformId) {
      const normalizedId = extractPlatformId(game.platformId)
      const iconUrl = getPlatformIcon(normalizedId)
      // Skip re-creating object if nothing changed
      if (iconUrl && game.platformIcon === iconUrl) return game
      return {
        ...game,
        platform: game.platform || getPlatformDisplayName(normalizedId),
        platformId: normalizedId,
        platformIcon: iconUrl,
      }
    }

    // Try to match from game.platform string (e.g. "meta", "Meta Quest")
    if (game.platform) {
      const matchedPlatform = activePlatforms.find(p =>
        p.display_name?.toLowerCase() === game.platform?.toLowerCase() ||
        p.name?.toLowerCase() === game.platform?.toLowerCase() ||
        p.id?.toLowerCase() === game.platform?.toLowerCase()
      )
      if (matchedPlatform) {
        return {
          ...game,
          platform: getPlatformDisplayName(matchedPlatform.id),
          platformId: matchedPlatform.id,
          platformIcon: getPlatformIcon(matchedPlatform.id),
        }
      }
    }

    // Try platform_id field
    if (game.platform_id) {
      const normalizedId = extractPlatformId(game.platform_id)
      return {
        ...game,
        platform: getPlatformDisplayName(normalizedId),
        platformId: normalizedId,
        platformIcon: getPlatformIcon(normalizedId),
      }
    }

    // Use the active platform filter if set
    if (platformFilter) {
      const normalizedId = extractPlatformId(platformFilter)
      return {
        ...game,
        platform: getPlatformDisplayName(normalizedId),
        platformId: normalizedId,
        platformIcon: getPlatformIcon(normalizedId),
      }
    }

    // Default to first active platform
    if (activePlatforms.length > 0) {
      const def = activePlatforms[0]
      return {
        ...game,
        platform: getPlatformDisplayName(def.id),
        platformId: def.id,
        platformIcon: getPlatformIcon(def.id),
      }
    }

    return game
  })
}

// In-memory (module-level) home-page section state: platform pills, filters and
// pagination for Most Tracked Games / Top Performer Apps. It survives in-app
// navigation (e.g. opening an app and coming back) but is wiped on a full page
// refresh, so a reload always lands on the default "all platforms" view.
// Deliberately NOT sessionStorage, which would also restore it after a refresh.
const dashboardSectionState = {
  trackedFilters: {},
  trackedFiltersRaw: {},
  topPerfFilters: {},
  topPerfFiltersRaw: {},
  pages: {},
}

const OptimizedDashboard = () => {
  const { measureOperation } = usePerformanceMonitor("OptimizedDashboard")
  const { trackEvent } = useAnalytics()
  const { filterSections } = useFilters()
  const { platforms, getActivePlatforms, getPlatformIcon, getPlatformDisplayName } = usePlatforms()
  // Memoize active platforms so downstream useMemo hooks re-run when
  // PlatformsContext finishes its async fetch.
  const activePlatforms = useMemo(() => getActivePlatforms(), [platforms, getActivePlatforms])
  // Tracks in-flight fetch requests so we ignore out-of-order responses
  const topPerfFetchRequestId = React.useRef(0)
  // Stable per-login/session identifier. Logout clears sessionStorage, so a new
  // login will get a new session id. We include this in React Query keys so
  // each login triggers fresh queries at least once, even within the same tab.
  const [dashboardSessionId] = React.useState(() => {
    try {
      const existing = sessionStorage.getItem("dashboard_session_id")
      if (existing) return existing
      const id = `${Date.now()}:${Math.random().toString(36).slice(2)}`
      sessionStorage.setItem("dashboard_session_id", id)
      return id
    } catch (_) {
      return "no-session-storage"
    }
  })
  // Track window width for responsive design
  const [windowWidth, setWindowWidth] = React.useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  )

  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const isMobile = windowWidth < 768

  // Calculate responsive gap between cards
  const cardGap = React.useMemo(() => {
    if (windowWidth < 768) return 12 // Mobile - keep original
    if (windowWidth < 1200) return 0.5 // Tablet - 0.5px gap
    // Desktop - scale with screen size (0.5px to 1.5px)
    return Math.min(1.5, 0.5 + (windowWidth - 1200) / 1000)
  }, [windowWidth])

  const cardGapHalf = cardGap / 2 // Half gap for padding (each side)

  // Identify if current session is browsing as a Guest, or isn't logged in
  // at all - anonymous visitors get the same restricted view as guests.
  const isGuest = useMemo(() => {
    try {
      const authToken = localStorage.getItem("authToken")
      if (!authToken) return true
      const authUser = JSON.parse(localStorage.getItem("authUser"))
      return (
        authUser?.data?.is_guest === true || authUser?.is_guest === true
      )
    } catch (_) {
      return false
    }
  }, [])

  // Set document title
  // React.useEffect(() => {
  //   document.title = "Keyquest | VR SEO Intelligence Tools Powered by AI & Big Data"
  // }, [])

  // NOTE: Trending and New Releases dashboard API calls were removed
  // from this component to avoid hitting:
  // - /tracker/stats/meta/trending
  // If needed in future, reintroduce them via React Query hooks here.

  // Filters for Most Tracked Games - initialize before tracked apps query.
  // Kept in dashboardSectionState (not sessionStorage) so a refresh resets them.
  const [showFilters, setShowFilters] = useState(false)
  const [trackedFilters, setTrackedFilters] = useState(() => dashboardSectionState.trackedFilters)
  // Keep the raw UI selection shape so the FilterPanel can reopen with previous choices
  const [trackedFiltersRaw, setTrackedFiltersRaw] = useState(() => dashboardSectionState.trackedFiltersRaw)

  // Applies a raw filter-panel selection (platform/genre/price/etc.) to the Most
  // Tracked Games query: remembers it in memory, converts it to the API's filter shape and resets to
  // page 1. Shared by the FilterPanel modal's onApply, the "remove filter"
  // chip buttons, and the platform pill row (which toggles just `platform` without
  // opening the modal at all).
  const applyTrackedFilters = (sel) => {
    const newFiltersRaw = sel || {};
    setTrackedFiltersRaw(newFiltersRaw);
    dashboardSectionState.trackedFiltersRaw = newFiltersRaw;

    // Convert to API format
    const apiFilters = {};
    if (newFiltersRaw.price) apiFilters.price = newFiltersRaw.price;
    if (newFiltersRaw.rating) apiFilters.rating = newFiltersRaw.rating;
    if (newFiltersRaw.revenue) apiFilters.revenue = newFiltersRaw.revenue;
    if (newFiltersRaw.release_date) apiFilters.release_date = newFiltersRaw.release_date;
    if (newFiltersRaw.genre) apiFilters.genre = newFiltersRaw.genre;
    if (newFiltersRaw.category) apiFilters.category = newFiltersRaw.category;
    if (newFiltersRaw.bundle) apiFilters.bundle = newFiltersRaw.bundle;
    if (newFiltersRaw.number_of_players) apiFilters.number_of_players = newFiltersRaw.number_of_players;
    if (newFiltersRaw.platform && newFiltersRaw.platform.length) apiFilters.platform = newFiltersRaw.platform;
    if (newFiltersRaw.sort_by) apiFilters.sort_by = newFiltersRaw.sort_by;
    else if (newFiltersRaw.sort) apiFilters.sort_by = newFiltersRaw.sort;

    setTrackedFilters(apiFilters);
    dashboardSectionState.trackedFilters = apiFilters;
    setTrackedPage(1);
  };

  // Handler to remove individual tracked filter
  const handleRemoveTrackedFilter = (key) => {
    const newFiltersRaw = { ...(trackedFiltersRaw || {}) };
    if (Object.prototype.hasOwnProperty.call(newFiltersRaw, key)) {
      delete newFiltersRaw[key];
      trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_FILTER_REMOVED, {
        section: "most_tracked",
        filter_key: key,
      })
      applyTrackedFilters(newFiltersRaw);
    }
  };

  // Single-select platform pill row next to the "Most Tracked Games" title — picking
  // one platform replaces whatever was selected before (only one active at a time,
  // matching the Custom Insights pages); clicking the already-active platform
  // clears the filter back to "all". Still applied without opening the Filters modal.
  const handleTrackedPlatformToggle = (platformId) => {
    const current = Array.isArray(trackedFiltersRaw?.platform) ? trackedFiltersRaw.platform : [];
    const isAlreadyActive = current.length === 1 && current[0] === platformId;
    const next = isAlreadyActive ? [] : [platformId];
    const newFiltersRaw = { ...(trackedFiltersRaw || {}) };
    if (next.length) newFiltersRaw.platform = next;
    else delete newFiltersRaw.platform;
    trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_PLATFORM_TOGGLED, {
      section: "most_tracked",
      platform: next.length ? platformId : undefined,
    })
    applyTrackedFilters(newFiltersRaw);
  };
  // Pagination for Most Tracked apps (computed from visible height)
  const [trackedPage, setTrackedPage] = useState(1)
  // Items per page for Most Tracked: show 4 items per page on all devices
  const trackedPerPage = React.useMemo(() => 4, [])
  // Timestamp for when tracked apps were last updated
  const [trackedLastUpdated, setTrackedLastUpdated] = useState(null)
  // Refs and state to compute a scrollable right list that matches left height
  const leftMainRef = React.useRef(null)
  const rightHeaderRef = React.useRef(null)
  const rightListRef = React.useRef(null)
  const [rightScrollHeight, setRightScrollHeight] = useState(null)
  // Refs for filter buttons to position FilterPanel
  const trackedFilterButtonRef = useRef(null)
  const topPerfFilterButtonRef = useRef(null)

  // Top Performer Apps (performance) filters and timestamp. Also kept in
  // dashboardSectionState, so a refresh resets this section to its default state too.
  const [showTopPerfFilters, setShowTopPerfFilters] = useState(false)
  const [topPerfFilters, setTopPerfFilters] = useState(() => dashboardSectionState.topPerfFilters)
  const [topPerfFiltersRaw, setTopPerfFiltersRaw] = useState(() => dashboardSectionState.topPerfFiltersRaw)
  const [topPerfLastUpdated, setTopPerfLastUpdated] = useState(null)

  // Applies a raw filter-panel selection (platform/genre/price/etc.) to the Top
  // Performer Apps query: remembers it in memory, converts it to the API's filter shape and resets to
  // page 1. Shared by the FilterPanel modal's onApply, the "remove
  // filter" chip buttons, and the platform pill row (which toggles just
  // `platform` without opening the modal at all) - same pattern as
  // applyTrackedFilters above.
  const applyTopPerfFilters = (sel) => {
    const newFiltersRaw = sel || {};
    setTopPerfFiltersRaw(newFiltersRaw);
    dashboardSectionState.topPerfFiltersRaw = newFiltersRaw;

    // Convert to API format
    const apiFilters = {};
    const normalize = v => Array.isArray(v) ? v.join(",") : v;

    if (newFiltersRaw.price) apiFilters.price = normalize(newFiltersRaw.price);
    if (newFiltersRaw.rating) apiFilters.rating = normalize(newFiltersRaw.rating);
    if (newFiltersRaw.revenue) apiFilters.revenue = normalize(newFiltersRaw.revenue);
    if (newFiltersRaw.release_date) apiFilters.release_date = normalize(newFiltersRaw.release_date);
    if (newFiltersRaw.genre) apiFilters.genre = normalize(newFiltersRaw.genre);
    if (newFiltersRaw.bundle) apiFilters.bundle = normalize(newFiltersRaw.bundle);
    if (newFiltersRaw.platform) apiFilters.platform = normalize(newFiltersRaw.platform);

    if (newFiltersRaw.number_of_players) {
      const mapPlayers = {
        Singleplayer: "Single User",
        Multiplayer: "Multiplayer",
        "Co-op": "Co-op",
      };
      const v = Array.isArray(newFiltersRaw.number_of_players)
        ? newFiltersRaw.number_of_players[0]
        : newFiltersRaw.number_of_players;
      apiFilters.number_of_players = mapPlayers[v] || v;
    }

    if (newFiltersRaw.sort_by || newFiltersRaw.sort) {
      const rawSort = newFiltersRaw.sort_by || newFiltersRaw.sort;
      const s = Array.isArray(rawSort) ? rawSort[0] : rawSort;
      apiFilters.sort_by = String(s).replace(/\?/g, "");
    }

    setTopPerfFilters(apiFilters);
    dashboardSectionState.topPerfFilters = apiFilters;
    setTopPerfPage(1);
  };

  // Handler to remove individual top performer filter
  const handleRemoveTopPerfFilter = (key) => {
    const newFiltersRaw = { ...(topPerfFiltersRaw || {}) };
    if (Object.prototype.hasOwnProperty.call(newFiltersRaw, key)) {
      delete newFiltersRaw[key];
      trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_FILTER_REMOVED, {
        section: "top_performer",
        filter_key: key,
      })
      applyTopPerfFilters(newFiltersRaw);
    }
  };

  // Single-select platform pill row next to the "Top Performer Apps" title —
  // picking one platform replaces whatever was selected before (only one
  // active at a time); clicking the already-active platform clears the
  // filter back to "all". Same behavior as handleTrackedPlatformToggle above,
  // applied without opening the Filters modal.
  const handleTopPerfPlatformToggle = (platformId) => {
    const current = Array.isArray(topPerfFiltersRaw?.platform) ? topPerfFiltersRaw.platform : [];
    const isAlreadyActive = current.length === 1 && current[0] === platformId;
    const next = isAlreadyActive ? [] : [platformId];
    const newFiltersRaw = { ...(topPerfFiltersRaw || {}) };
    if (next.length) newFiltersRaw.platform = next;
    else delete newFiltersRaw.platform;
    trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_PLATFORM_TOGGLED, {
      section: "top_performer",
      platform: next.length ? platformId : undefined,
    })
    applyTopPerfFilters(newFiltersRaw);
  };
  // Pagination for Top Performer apps (client-side paging of returned array)
  const [topPerfPage, setTopPerfPage] = useState(1)
  // Responsive items per page: 10 for web/tablet, 5 for mobile
  const topPerfPerPage = React.useMemo(() => (isMobile ? 5 : 10), [isMobile])
  // Inline Top Performer selected app and graph
  const [selectedTopAppId, setSelectedTopAppId] = useState(null)
  const [selectedTopAppData, setSelectedTopAppData] = useState(null)
  const [selectedTopAppLoading, setSelectedTopAppLoading] = useState(false)

  // Tracked apps query
  const {
    data: trackedAppsData = [],
    isLoading: trackedLoading,
    isFetching: trackedFetching,
    error: trackedError,
  } = useQuery({
    // Include filters and a per-login session id in the query key so changes
    // re-fetch (API returns full 20 items) and each login triggers a fresh call.
    queryKey: [
      ...queryKeys.dashboard.trackedApps(),
      dashboardSessionId,
      JSON.stringify(trackedFilters),
    ],
    queryFn: async () => {
      return measureOperation("fetchTrackedApps", async () => {
        const storageKey =
          "trackedApps" + JSON.stringify(trackedFilters || {})
        const sessionAuthKey = "dashboard_auth_token"

        // Read current auth token and the token associated with the cached data
        let authToken = null
        let cachedAuthToken = null
        try {
          authToken = localStorage.getItem("authToken") || null
        } catch (_) { }
        try {
          cachedAuthToken = sessionStorage.getItem(sessionAuthKey) || null
        } catch (_) { }

        // If this browser session already has cached data for this filter set
        // AND it was generated under the same auth token, return it without
        // hitting the backend again.
        if (cachedAuthToken && authToken && cachedAuthToken === authToken) {
          try {
            const storedData = sessionStorage.getItem(storageKey)
            if (storedData) {
              const parsed = JSON.parse(storedData)
              let arr = Array.isArray(parsed)
                ? parsed
                : Array.isArray(parsed?.data)
                  ? parsed.data
                  : []
              if (arr.length > 0) {
                // Add platform information to cached games
                const platformFilter = topPerfFilters?.platform || trackedFilters?.platform;
                const activePlatforms = getActivePlatforms();
                
                // Helper to extract platform ID from array or string
                const extractPlatformId = (platformData) => {
                  if (Array.isArray(platformData) && platformData.length > 0) {
                    return platformData[0]; // Take first element if array
                  }
                  return platformData; // Return as-is if string
                };
                
                arr = arr.map(game => {
                  if (game.platformId && !game.platformIcon) {
                    const normalizedPlatformId = extractPlatformId(game.platformId);
                    const platformIconUrl = getPlatformIcon ? getPlatformIcon(normalizedPlatformId) : null;
                    const platformDisplayName = game.platform || (getPlatformDisplayName ? getPlatformDisplayName(normalizedPlatformId) : normalizedPlatformId);
                    return { ...game, platform: platformDisplayName, platformId: normalizedPlatformId, platformIcon: platformIconUrl };
                  }
                  
                  // Try to match platform from game.platform field
                  if (game.platform && !game.platformId) {
                    const matchedPlatform = activePlatforms.find(p => 
                      p.display_name?.toLowerCase() === game.platform?.toLowerCase() ||
                      p.name?.toLowerCase() === game.platform?.toLowerCase() ||
                      p.id?.toLowerCase() === game.platform?.toLowerCase()
                    );
                    if (matchedPlatform && !game.platformIcon) {
                      const platformIconUrl = getPlatformIcon ? getPlatformIcon(matchedPlatform.id) : null;
                      const platformDisplayName = getPlatformDisplayName ? getPlatformDisplayName(matchedPlatform.id) : matchedPlatform.display_name;
                      return { ...game, platform: platformDisplayName, platformId: matchedPlatform.id, platformIcon: platformIconUrl };
                    }
                  }
                  
                  // Only use platform filter if game has no platform information
                  if (!game.platformId && !game.platform && platformFilter) {
                    const normalizedPlatformId = extractPlatformId(platformFilter);
                    const platformIconUrl = getPlatformIcon ? getPlatformIcon(normalizedPlatformId) : null;
                    const platformDisplayName = getPlatformDisplayName ? getPlatformDisplayName(normalizedPlatformId) : normalizedPlatformId;
                    return { 
                      ...game, 
                      platform: platformDisplayName,
                      platformId: normalizedPlatformId,
                      platformIcon: platformIconUrl 
                    };
                  }
                  return game;
                });
                
                // Also ensure updated_at from cache is available for useEffect
                const updatedAtKey = storageKey + "_updated_at"
                const cachedUpdatedAt = sessionStorage.getItem(updatedAtKey)
                if (cachedUpdatedAt) {
                  // Keep it in sessionStorage for the useEffect to pick up
                  sessionStorage.setItem(updatedAtKey, cachedUpdatedAt)
                }
                return arr
              }
            }
          } catch (_) {
            // ignore parse/storage errors and fall through to fresh fetch
          }
        }

        // Either first login for this session, auth token changed (logout/login),
        // or there is no cached data for this filter set: fetch fresh data and
        // bind it to the current auth token in sessionStorage.
        try {
          const response = await apiService.fetchTrackedApps(trackedFilters)
          let games = response?.data || response || []
          
          // Add platform information to games based on filters or existing data
          const activePlatforms = getActivePlatforms();
          const platformFilter = trackedFilters?.platform;
          
          games = games.map(game => {
            // Helper to extract platform ID from array or string
            const extractPlatformId = (platformData) => {
              if (Array.isArray(platformData) && platformData.length > 0) {
                return platformData[0]; // Take first element if array
              }
              return platformData; // Return as-is if string
            };
            
            // If game already has platform info, ensure platformId is set for icon lookup
            if (game.platformId) {
              const normalizedPlatformId = extractPlatformId(game.platformId);
              const platformIconUrl = getPlatformIcon ? getPlatformIcon(normalizedPlatformId) : null;
              const platformDisplayName = game.platform || (getPlatformDisplayName ? getPlatformDisplayName(normalizedPlatformId) : normalizedPlatformId);
              return {
                ...game,
                platform: platformDisplayName,
                platformId: normalizedPlatformId,
                platformIcon: platformIconUrl || game.platformIcon
              };
            }
            
            // Try to infer platform from game's platform display name
            if (game.platform) {
              // Try to find matching platform by display name or name
              const matchedPlatform = activePlatforms.find(p => 
                p.display_name?.toLowerCase() === game.platform?.toLowerCase() ||
                p.name?.toLowerCase() === game.platform?.toLowerCase() ||
                p.id?.toLowerCase() === game.platform?.toLowerCase()
              );
              if (matchedPlatform) {
                const platformIconUrl = getPlatformIcon ? getPlatformIcon(matchedPlatform.id) : null;
                const platformDisplayName = getPlatformDisplayName ? getPlatformDisplayName(matchedPlatform.id) : matchedPlatform.display_name;
                return {
                  ...game,
                  platform: platformDisplayName,
                  platformId: matchedPlatform.id,
                  platformIcon: platformIconUrl
                };
              }
            }
            
            // Try to infer platform from game data if available
            const gamePlatformId = game.platform_id || game.platformId;
            if (gamePlatformId) {
              const normalizedPlatformId = extractPlatformId(gamePlatformId);
              const platformIconUrl = getPlatformIcon ? getPlatformIcon(normalizedPlatformId) : null;
              const platformDisplayName = getPlatformDisplayName ? getPlatformDisplayName(normalizedPlatformId) : normalizedPlatformId;
              return {
                ...game,
                platform: platformDisplayName,
                platformId: normalizedPlatformId,
                platformIcon: platformIconUrl
              };
            }
            
            // Only use platform filter if game has no platform information at all
            if (platformFilter && !game.platform && !game.platformId && !game.platform_id) {
              const normalizedPlatformId = extractPlatformId(platformFilter);
              const platformIconUrl = getPlatformIcon ? getPlatformIcon(normalizedPlatformId) : null;
              const platformDisplayName = getPlatformDisplayName ? getPlatformDisplayName(normalizedPlatformId) : normalizedPlatformId;
              return {
                ...game,
                platform: platformDisplayName,
                platformId: normalizedPlatformId,
                platformIcon: platformIconUrl
              };
            }
            
            // Default to first active platform if available
            if (activePlatforms.length > 0) {
              const defaultPlatform = activePlatforms[0];
              const platformIconUrl = getPlatformIcon ? getPlatformIcon(defaultPlatform.id) : null;
              const platformDisplayName = getPlatformDisplayName ? getPlatformDisplayName(defaultPlatform.id) : defaultPlatform.name;
              return {
                ...game,
                platform: platformDisplayName,
                platformId: defaultPlatform.id,
                platformIcon: platformIconUrl
              };
            }
            
            return game;
          });
          
          const updatedAt = response?.updated_at || null

          sessionStorage.setItem(storageKey, JSON.stringify(games))
          // Store updated_at timestamp separately for display
          if (updatedAt) {
            const updatedAtKey = storageKey + "_updated_at"
            sessionStorage.setItem(updatedAtKey, updatedAt)
          }
          if (authToken) {
            sessionStorage.setItem(sessionAuthKey, authToken)
          } else {
            sessionStorage.removeItem(sessionAuthKey)
          }
          return games
        } catch (error) {
          console.error("Error fetching tracked apps:", error)
          // If it's a guest restriction error (tagged by api_helper interceptor), rethrow it
          // so useQuery can catch it and provide it as `trackedError`.
          if (error.isGuestRestricted) throw error

          // On error, fall back to any previously cached data in sessionStorage
          try {
            const storedData = sessionStorage.getItem(storageKey)
            if (storedData) {
              const parsed = JSON.parse(storedData)
              const arr = Array.isArray(parsed)
                ? parsed
                : Array.isArray(parsed?.data)
                  ? parsed.data
                  : []
              // Also ensure updated_at from cache is available
              const updatedAtKey = storageKey + "_updated_at"
              const cachedUpdatedAt = sessionStorage.getItem(updatedAtKey)
              if (cachedUpdatedAt) {
                sessionStorage.setItem(updatedAtKey, cachedUpdatedAt)
              }
              return arr
            }
          } catch (_) {
            // ignore parse/storage errors and fall through to empty array
          }
          return []
        }
      })
    },
    // Let React Query handle in-memory caching; avoid extra refetches on focus/reconnect.
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 15,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  // Reactively map platform info onto tracked apps whenever platforms context
  // finishes loading. This fixes icons disappearing on hard refresh.
  const mappedTrackedApps = useMemo(() => {
    const arr = Array.isArray(trackedAppsData) ? trackedAppsData : []
    return mapPlatformInfoToGames(arr, getPlatformIcon, getPlatformDisplayName, activePlatforms, trackedFilters?.platform)
  }, [trackedAppsData, activePlatforms, getPlatformIcon, getPlatformDisplayName, trackedFilters])

  // Client-side pagination: API returns 20 items; distribute into pages of `trackedPerPage`
  const fullTrackedApps = mappedTrackedApps
  const totalPages = Math.max(
    1,
    Math.ceil(fullTrackedApps.length / trackedPerPage),
  )
  const currentPageData = fullTrackedApps.slice(
    (trackedPage - 1) * trackedPerPage,
    trackedPage * trackedPerPage,
  )

  // The user's own tracked apps (same list shown on the App Tracking page), used for the
  // "jump straight into a tracked app" previews in the Explore More Tools cards below.
  const {
    data: myTrackedApps = [],
    isLoading: myTrackedAppsLoading,
    isFetching: myTrackedAppsFetching,
  } = useQuery({
    queryKey: [...queryKeys.dashboard.myTrackedApps(), dashboardSessionId],
    queryFn: () => apiService.fetchMyTrackedApps(),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  })

  // Top performance apps query (KeyQuest App Score)
  const {
    data: topPerformanceApps = [],
    isLoading: topPerfLoading,
    error: topPerfError,
  } = useQuery({
    queryKey: [
      ...queryKeys.dashboard.topPerformance(),
      dashboardSessionId,
      JSON.stringify(topPerfFilters),
    ],
    queryFn: async () => {
      return measureOperation("fetchTopPerformance", async () => {
        const storageKey =
          "topPerformance" + JSON.stringify(topPerfFilters || {})
        const sessionAuthKey = "dashboard_auth_token"

        // Read current auth token and the token associated with the cached data
        let authToken = null
        let cachedAuthToken = null
        try {
          authToken = localStorage.getItem("authToken") || null
        } catch (_) { }
        try {
          cachedAuthToken = sessionStorage.getItem(sessionAuthKey) || null
        } catch (_) { }

        // If this browser session already has cached data for this filter set
        // AND it was generated under the same auth token, return it without
        // hitting the backend again.
        if (cachedAuthToken && authToken && cachedAuthToken === authToken) {
          try {
            const storedData = sessionStorage.getItem(storageKey)
            if (storedData) {
              const parsed = JSON.parse(storedData)
              let arr = Array.isArray(parsed)
                ? parsed
                : Array.isArray(parsed?.data)
                  ? parsed.data
                  : []
              if (arr.length > 0) {
                // Add platform information to cached games
                const platformFilter = topPerfFilters?.platform || trackedFilters?.platform;
                const activePlatforms = getActivePlatforms();
                
                // Helper to extract platform ID from array or string
                const extractPlatformId = (platformData) => {
                  if (Array.isArray(platformData) && platformData.length > 0) {
                    return platformData[0]; // Take first element if array
                  }
                  return platformData; // Return as-is if string
                };
                
                arr = arr.map(game => {
                  if (game.platformId && !game.platformIcon) {
                    const normalizedPlatformId = extractPlatformId(game.platformId);
                    const platformIconUrl = getPlatformIcon ? getPlatformIcon(normalizedPlatformId) : null;
                    const platformDisplayName = game.platform || (getPlatformDisplayName ? getPlatformDisplayName(normalizedPlatformId) : normalizedPlatformId);
                    return { ...game, platform: platformDisplayName, platformId: normalizedPlatformId, platformIcon: platformIconUrl };
                  }
                  
                  // Try to match platform from game.platform field
                  if (game.platform && !game.platformId) {
                    const matchedPlatform = activePlatforms.find(p => 
                      p.display_name?.toLowerCase() === game.platform?.toLowerCase() ||
                      p.name?.toLowerCase() === game.platform?.toLowerCase() ||
                      p.id?.toLowerCase() === game.platform?.toLowerCase()
                    );
                    if (matchedPlatform && !game.platformIcon) {
                      const platformIconUrl = getPlatformIcon ? getPlatformIcon(matchedPlatform.id) : null;
                      const platformDisplayName = getPlatformDisplayName ? getPlatformDisplayName(matchedPlatform.id) : matchedPlatform.display_name;
                      return { ...game, platform: platformDisplayName, platformId: matchedPlatform.id, platformIcon: platformIconUrl };
                    }
                  }
                  
                  // Only use platform filter if game has no platform information
                  if (!game.platformId && !game.platform && platformFilter) {
                    const normalizedPlatformId = extractPlatformId(platformFilter);
                    const platformIconUrl = getPlatformIcon ? getPlatformIcon(normalizedPlatformId) : null;
                    const platformDisplayName = getPlatformDisplayName ? getPlatformDisplayName(normalizedPlatformId) : normalizedPlatformId;
                    return { 
                      ...game, 
                      platform: platformDisplayName,
                      platformId: normalizedPlatformId,
                      platformIcon: platformIconUrl 
                    };
                  }
                  return game;
                });
                
                // Also ensure updated_at from cache is available for useEffect
                const updatedAtKey = storageKey + "_updated_at"
                const cachedUpdatedAt = sessionStorage.getItem(updatedAtKey)
                if (cachedUpdatedAt) {
                  // Keep it in sessionStorage for the useEffect to pick up
                  sessionStorage.setItem(updatedAtKey, cachedUpdatedAt)
                }
                return arr
              }
            }
          } catch (_) {
            // ignore parse/storage errors and fall through to fresh fetch
          }
        }

        // Either first login for this session, auth token changed (logout/login),
        // or there is no cached data for this filter set: fetch fresh data and
        // bind it to the current auth token in sessionStorage.
        try {
          const response = await apiService.fetchTopPerformance(topPerfFilters)
          let games = response?.data || response || []
          
          // Add platform information to games based on filters or existing data
          const activePlatforms = getActivePlatforms();
          const platformFilter = topPerfFilters?.platform;
          
          games = games.map(game => {
            // Helper to extract platform ID from array or string
            const extractPlatformId = (platformData) => {
              if (Array.isArray(platformData) && platformData.length > 0) {
                return platformData[0]; // Take first element if array
              }
              return platformData; // Return as-is if string
            };
            
            // If game already has platform info, ensure platformId is set for icon lookup
            if (game.platformId) {
              const normalizedPlatformId = extractPlatformId(game.platformId);
              const platformIconUrl = getPlatformIcon ? getPlatformIcon(normalizedPlatformId) : null;
              const platformDisplayName = game.platform || (getPlatformDisplayName ? getPlatformDisplayName(normalizedPlatformId) : normalizedPlatformId);
              return {
                ...game,
                platform: platformDisplayName,
                platformId: normalizedPlatformId,
                platformIcon: platformIconUrl || game.platformIcon
              };
            }
            
            // Try to infer platform from game's platform display name
            if (game.platform) {
              // Try to find matching platform by display name or name
              const matchedPlatform = activePlatforms.find(p => 
                p.display_name?.toLowerCase() === game.platform?.toLowerCase() ||
                p.name?.toLowerCase() === game.platform?.toLowerCase() ||
                p.id?.toLowerCase() === game.platform?.toLowerCase()
              );
              if (matchedPlatform) {
                const platformIconUrl = getPlatformIcon ? getPlatformIcon(matchedPlatform.id) : null;
                const platformDisplayName = getPlatformDisplayName ? getPlatformDisplayName(matchedPlatform.id) : matchedPlatform.display_name;
                return {
                  ...game,
                  platform: platformDisplayName,
                  platformId: matchedPlatform.id,
                  platformIcon: platformIconUrl
                };
              }
            }
            
            // Try to infer platform from game data if available
            const gamePlatformId = game.platform_id || game.platformId;
            if (gamePlatformId) {
              const normalizedPlatformId = extractPlatformId(gamePlatformId);
              const platformIconUrl = getPlatformIcon ? getPlatformIcon(normalizedPlatformId) : null;
              const platformDisplayName = getPlatformDisplayName ? getPlatformDisplayName(normalizedPlatformId) : normalizedPlatformId;
              return {
                ...game,
                platform: platformDisplayName,
                platformId: normalizedPlatformId,
                platformIcon: platformIconUrl
              };
            }
            
            // Only use platform filter if game has no platform information at all
            if (platformFilter && !game.platform && !game.platformId && !game.platform_id) {
              const normalizedPlatformId = extractPlatformId(platformFilter);
              const platformIconUrl = getPlatformIcon ? getPlatformIcon(normalizedPlatformId) : null;
              const platformDisplayName = getPlatformDisplayName ? getPlatformDisplayName(normalizedPlatformId) : normalizedPlatformId;
              return {
                ...game,
                platform: platformDisplayName,
                platformId: normalizedPlatformId,
                platformIcon: platformIconUrl
              };
            }
            
            // Default to first active platform if available
            if (activePlatforms.length > 0) {
              const defaultPlatform = activePlatforms[0];
              const platformIconUrl = getPlatformIcon ? getPlatformIcon(defaultPlatform.id) : null;
              const platformDisplayName = getPlatformDisplayName ? getPlatformDisplayName(defaultPlatform.id) : defaultPlatform.name;
              return {
                ...game,
                platform: platformDisplayName,
                platformId: defaultPlatform.id,
                platformIcon: platformIconUrl
              };
            }
            
            return game;
          });
          
          const updatedAt = response?.updated_at || null

          sessionStorage.setItem(storageKey, JSON.stringify(games))
          // Store updated_at timestamp separately for display
          if (updatedAt) {
            const updatedAtKey = storageKey + "_updated_at"
            sessionStorage.setItem(updatedAtKey, updatedAt)
          }
          if (authToken) {
            sessionStorage.setItem(sessionAuthKey, authToken)
          } else {
            sessionStorage.removeItem(sessionAuthKey)
          }
          return games
        } catch (error) {
          console.error("Error fetching top performance apps:", error)
          // If it's a guest restriction error (tagged by api_helper interceptor), rethrow it
          // so useQuery can catch it and provide it as `topPerfError`.
          if (error.isGuestRestricted) throw error

          // On error, fall back to any previously cached data in sessionStorage
          try {
            const storedData = sessionStorage.getItem(storageKey)
            if (storedData) {
              const parsed = JSON.parse(storedData)
              const arr = Array.isArray(parsed)
                ? parsed
                : Array.isArray(parsed?.data)
                  ? parsed.data
                  : []
              // Also ensure updated_at from cache is available
              const updatedAtKey = storageKey + "_updated_at"
              const cachedUpdatedAt = sessionStorage.getItem(updatedAtKey)
              if (cachedUpdatedAt) {
                sessionStorage.setItem(updatedAtKey, cachedUpdatedAt)
              }
              return arr
            }
          } catch (_) {
            // ignore parse/storage errors and fall through to empty array
          }
          return []
        }
      })
    },
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 15,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  // Reactively map platform info onto top performance apps whenever platforms context
  // finishes loading. This fixes icons disappearing on hard refresh.
  const mappedTopPerformanceApps = useMemo(() => {
    const arr = Array.isArray(topPerformanceApps) ? topPerformanceApps : []
    return mapPlatformInfoToGames(arr, getPlatformIcon, getPlatformDisplayName, activePlatforms, topPerfFilters?.platform)
  }, [topPerformanceApps, activePlatforms, getPlatformIcon, getPlatformDisplayName, topPerfFilters])

  // Top Performer client-side pagination variables (computed from full response)
  // Deduplicate Top Performer apps by `item_id` to avoid duplicate cards (react-slick clones
  // can make duplicate-looking entries when the source contains repeats). Preserve order.
  const _rawTop = mappedTopPerformanceApps
  const seenTopIds = new Set()
  const fullTop = _rawTop.filter(app => {
    if (!app || !app.item_id) return false
    if (seenTopIds.has(app.item_id)) return false
    seenTopIds.add(app.item_id)
    return true
  })

  // Calculate total pages: 10 items per page for web/tablet, 5 for mobile
  const topTotalPages = Math.max(1, Math.ceil(fullTop.length / topPerfPerPage))
  const topPageSafe = Math.min(Math.max(1, topPerfPage), topTotalPages)

  // Calculate slice indices: 10 items per page for web/tablet, 5 for mobile
  const topCurrent = fullTop.slice(
    (topPageSafe - 1) * topPerfPerPage,
    topPageSafe * topPerfPerPage,
  )

  // compute scroll height to show 5 items initially; measuring rendered item height
  React.useEffect(() => {
    const update = () => {
      try {
        // Prefer measuring an actual item so we show exactly 5 items.
        const listEl = rightListRef.current
        const firstItem = listEl
          ? listEl.querySelector(".track_game_boxes")
          : null

        if (firstItem) {
          const rect = firstItem.getBoundingClientRect()
          const itemHeight =
            rect.height || firstItem.offsetHeight || firstItem.clientHeight || 0
          if (itemHeight) {
            const visibleItems = 4 // show 4 items initially, reveal remaining on scroll
            const gapPerItem = 8 // buffer for margins/paddings
            const calc = Math.max(
              120,
              Math.round(
                itemHeight * visibleItems + gapPerItem * (visibleItems - 1),
              ),
            )
            setRightScrollHeight(calc)
            return
          }
        }

        // Fallback: measure left main container and subtract header + pagination area
        const leftH = leftMainRef.current
          ? leftMainRef.current.clientHeight
          : null
        const headerH = rightHeaderRef.current
          ? rightHeaderRef.current.clientHeight
          : 0
        const paginationArea = 56 // approximate height reserved for pagination controls
        if (leftH) {
          const calc = Math.max(120, leftH - headerH - paginationArea - 8)
          setRightScrollHeight(calc)
          return
        }
      } catch (e) {
        // ignore
      }
    }

    // Run once after render and on relevant changes. Small timeout ensures DOM has painted.
    const t = setTimeout(update, 50)
    window.addEventListener("resize", update)
    // Also attempt a second try after a short delay in case items render slightly later
    const t2 = setTimeout(update, 250)
    return () => {
      clearTimeout(t)
      clearTimeout(t2)
      window.removeEventListener("resize", update)
    }
  }, [rightListRef, trackedAppsData, trackedLoading])

  // The per-page is fixed to 10 (API returns 20 items total: two pages of 10)

  // (Removed separate auto-open effect) - initial opening and filter-driven opening
  // are handled in the filter-driven effect below to avoid racing.

  // NOTE: auto-opening of the top performer after filters or page changes
  // is handled after the handler declaration (see effect below) to avoid TDZ
  // and ensure the click handler is defined before use.

  // Handler to fetch & show inline App Score graph for Top Performer apps
  const handleTopPerfClick = async (app, { forceRefresh = false } = {}) => {
    if (!app || !app.item_id) return

    // If the same app is clicked and not forcing refresh, toggle off
    if (selectedTopAppId === app.item_id && !forceRefresh) {
      setSelectedTopAppId(null)
      setSelectedTopAppData(null)
      return
    }

    // Only track genuine user clicks - `forceRefresh` is used internally to
    // auto-open the first app of a page after pagination, not a real click.
    if (!forceRefresh) {
      trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_APP_SELECTED, {
        section: "top_performer",
        app_id: app.item_id,
        app_name: app.name,
      })
    }

    setSelectedTopAppId(app.item_id)
    setSelectedTopAppLoading(true)
    // Increment request id and capture locally to ignore out-of-order responses
    topPerfFetchRequestId.current += 1
    const reqId = topPerfFetchRequestId.current
    try {
      const res = await apiService.fetchAppGraph(app.item_id)
      const payload = res?.data || res || []

      let normalized = []
      if (Array.isArray(payload)) normalized = payload
      else if (payload?.history?.historic_entries)
        normalized = payload.history.historic_entries
      else if (payload?.historic_entries) normalized = payload.historic_entries
      else normalized = payload

      // Map entries to include `created_at_date` in YYYY-MM-DD format (KTRLineChart expects this)
      const mapped = (Array.isArray(normalized) ? normalized : []).map(
        entry => {
          const epoch =
            entry.created_at || entry.created_at_date || entry.ts || null
          const dateObj = epoch ? new Date(Number(epoch) * 1000) : null
          const created_at_date = dateObj
            ? dateObj.toISOString().slice(0, 10)
            : entry.created_at_date || null
          return {
            ...entry,
            created_at_date,
          }
        },
      )

      // Only set data if this response is the latest request
      if (reqId === topPerfFetchRequestId.current) {
        setSelectedTopAppData(mapped)
      } else {
        // stale response, ignore
        console.warn("Ignored stale app_graph response", app.item_id)
      }
    } catch (err) {
      console.error("Error loading app graph:", err)
      // Only clear if this is latest request
      if (reqId === topPerfFetchRequestId.current) setSelectedTopAppData(null)
    } finally {
      if (reqId === topPerfFetchRequestId.current)
        setSelectedTopAppLoading(false)
    }
  }

  // When top performer page changes, auto-open first app's graph for that page
  React.useEffect(() => {
    try {
      if (
        !topPerfLoading &&
        Array.isArray(topCurrent) &&
        topCurrent.length > 0
      ) {
        // Always open the first app of the current page and force refresh
        handleTopPerfClick(topCurrent[0], { forceRefresh: true })
      } else {
        setSelectedTopAppId(null)
        setSelectedTopAppData(null)
      }
    } catch (e) {
      console.warn("Auto-opening top performer after page change failed", e)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topPageSafe, topPerfLoading, topPerformanceApps])

  // Memoize error state (currently only tracked apps contribute)
  const hasError = useMemo(
    () => trackedError,
    [trackedError],
  )

  if (hasError) {
    console.error("Dashboard errors:", {
      trackedError,
    })
  }

  // Update `trackedLastUpdated` when tracked apps change (use API's updated_at if available)
  React.useEffect(() => {
    try {
      if (
        !trackedLoading &&
        !trackedFetching &&
        Array.isArray(trackedAppsData) &&
        trackedAppsData.length > 0
      ) {
        // Try to get updated_at from sessionStorage (stored by queryFn)
        const storageKey = "trackedApps" + JSON.stringify(trackedFilters || {})
        const updatedAtKey = storageKey + "_updated_at"
        const storedUpdatedAt = sessionStorage.getItem(updatedAtKey)

        if (storedUpdatedAt) {
          // Parse the ISO timestamp string and convert to timestamp
          const parsedDate = new Date(storedUpdatedAt)
          if (!isNaN(parsedDate.getTime())) {
            setTrackedLastUpdated(parsedDate.getTime())
            return
          }
        }
        // Fallback to current time if no updated_at available
        setTrackedLastUpdated(Date.now())
      }
    } catch (e) {
      // safe-guard
      console.warn("Error setting tracked last updated", e)
    }
  }, [trackedLoading, trackedFetching, trackedAppsData, trackedFilters])

  // Update top performance timestamp when data changes (use API's updated_at if available)
  React.useEffect(() => {
    try {
      if (!topPerfLoading) {
        const hasArrayData =
          Array.isArray(topPerformanceApps) && topPerformanceApps.length > 0
        const hasObjectData =
          topPerformanceApps &&
          typeof topPerformanceApps === "object" &&
          !Array.isArray(topPerformanceApps) &&
          Object.keys(topPerformanceApps).length > 0

        if (hasArrayData || hasObjectData) {
          // Try to get updated_at from sessionStorage (stored by queryFn)
          const storageKey = "topPerformance" + JSON.stringify(topPerfFilters || {})
          const updatedAtKey = storageKey + "_updated_at"
          const storedUpdatedAt = sessionStorage.getItem(updatedAtKey)

          if (storedUpdatedAt) {
            // Parse the ISO timestamp string and convert to timestamp
            const parsedDate = new Date(storedUpdatedAt)
            if (!isNaN(parsedDate.getTime())) {
              setTopPerfLastUpdated(parsedDate.getTime())
              return
            }
          }
          // Fallback to current time if no updated_at available
          setTopPerfLastUpdated(Date.now())
        }
      }
    } catch (e) {
      console.warn("Error setting top performance last updated", e)
    }
  }, [topPerfLoading, topPerformanceApps, topPerfFilters])

  // Reset to page 1 when switching between mobile/desktop view
  React.useEffect(() => {
    setTrackedPage(1)
  }, [isMobile])

  const SECTION_SKELETON_HEIGHT_DESKTOP = 86
  const SectionListSkeleton = ({ count = 4, height = SECTION_SKELETON_HEIGHT_DESKTOP }) => (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton
          key={i}
          height={height}
          className="mb-3"
          borderRadius={10}
          baseColor="#171821"
          highlightColor="#2a2b37"
          enableAnimation={true}
        />
      ))}
    </>
  )

  // Persist and restore pagination for Most Tracked and Top Performer sections.
  // We store the selected page in memory (dashboardSectionState) keyed by the
  // active filters so returning from a detail page restores the same page for the
  // same filter set, while a full refresh starts back on page 1.
  React.useEffect(() => {
    const key = "trackedPage_" + JSON.stringify(trackedFilters || {})
    const stored = dashboardSectionState.pages[key]
    if (stored) {
      const clamped = Math.min(Math.max(1, stored), totalPages)
      setTrackedPage(clamped)
    }
    // Only run when filters or available totalPages change
  }, [trackedFilters, totalPages])

  React.useEffect(() => {
    dashboardSectionState.pages["trackedPage_" + JSON.stringify(trackedFilters || {})] = trackedPage
  }, [trackedPage, trackedFilters])

  React.useEffect(() => {
    const key = "topPerfPage_" + JSON.stringify(topPerfFilters || {})
    const stored = dashboardSectionState.pages[key]
    if (stored) {
      const clamped = Math.min(Math.max(1, stored), topTotalPages)
      setTopPerfPage(clamped)
    }
  }, [topPerfFilters, topTotalPages])

  React.useEffect(() => {
    dashboardSectionState.pages["topPerfPage_" + JSON.stringify(topPerfFilters || {})] = topPerfPage
  }, [topPerfPage, topPerfFilters])

  return (
    <React.Fragment>
      <SEO
        title={PAGE_SEO.dashboard.title}
        description={PAGE_SEO.dashboard.description}
        keywords={PAGE_SEO.dashboard.keywords}
        canonical={generateCanonicalUrl("/dashboard")}
        robots={PAGE_SEO.dashboard.robots}
        structuredData={[STRUCTURED_DATA.organization, STRUCTURED_DATA.website]}
      />
      {/* Make chart export/download menu match dark theme and improve visibility */}
      <style>{`
        /* ApexCharts export menu overrides */
        .apexcharts-menu {
          background: #18181f !important;
          color: #fff !important;
          border: 1px solid rgba(255,255,255,0.06) !important;
          box-shadow: 0 8px 20px rgba(0,0,0,0.6) !important;
          padding: 6px !important;
          border-radius: 6px !important;
        }
        .apexcharts-menu .apexcharts-menu-item,
        .apexcharts-menu .apexcharts-menu-item a {
          color: #fff !important;
          background: transparent !important;
          font-weight: 600 !important;
        }
        .apexcharts-menu .apexcharts-menu-item:hover { background: rgba(255,255,255,0.03) !important; }
        .apexcharts-menu .apexcharts-menu-item[disabled],
        .apexcharts-menu .apexcharts-menu-item.apexcharts-menu-disabled {
          color: rgba(255,255,255,0.45) !important;
          opacity: 1 !important;
        }
        /* Ensure dropdown caret and separators are subtle */
        .apexcharts-menu-separator { background: rgba(255,255,255,0.06) !important; }

        /* Dashboard explore-tools cards */
        .dashboard-tool-card {
          transition: border-color 0.2s ease;
        }
        .dashboard-tool-card:hover {
          border-color: #F6255A !important;
        }
        .dashboard-tool-app-thumb img {
          transition: border-color 0.2s ease, transform 0.2s ease;
        }
        .dashboard-tool-app-thumb:hover img {
          border-color: #F6255A !important;
          transform: translateY(-2px);
        }

        /* Ensure Top Performer Apps header is visible on mobile */
        @media (max-width: 768px) {
          .trending_slider {
            overflow: visible !important;
          }
          .trending_slider ~ * {
            overflow: visible !important;
          }
          /* Add space between mobile header and carousel */
          .dashboard-responsive {
            padding-top: 24px !important;
            margin-top: 0 !important;
          }
          .dashboard-responsive .container-fluid {
            padding-top: 0 !important;
          }
          #dashboard-first-section {
            margin-top: 16px !important;
            padding-top: 0 !important;
          }
        }
      `}</style>
      <div className="page-content dashboard-responsive">
        <Container fluid>
          <h1 className="visually-hidden">Dashboard</h1>
          {/* Section 1: Two-column Tracked Games */}
          <Row
            className="gx-4 gy-4"
            style={{ marginTop: 0 }}
            id="dashboard-first-section"
          >
            {/* Left Column - 40% */}
            <Col xs={12} md={12} lg={5}>
              <div
                ref={leftMainRef}
                className={`rounded-3 custom-mobile-height game_dash_lt ${trackedError?.isGuestRestricted ? '' : 'h-100'}`}
              >
                {trackedError?.isGuestRestricted ? (
                  <GuestRestrictedPlaceholder 
                    height="400px"
                    subMessage="Sign in to view personalized gaming articles and AI-powered insights."
                  />
                ) : (trackedLoading || trackedFetching) ? (
                  <Skeleton
                    height={400}
                    borderRadius={10}
                    baseColor="#171821"
                    highlightColor="#2a2b37"
                    enableAnimation={true}
                  />
                ) : currentPageData && currentPageData.length > 0 ? (
                  <ArticleCardCarousel articles={currentPageData} />
                ) : (
                  <div style={{ padding: 24, color: "rgba(255,255,255,0.75)" }}>
                    No apps available
                  </div>
                )}
              </div>
            </Col>

            {/* Right Column - 60% */}
            <Col xs={12} md={12} lg={7}>
              <div
                className={`border rounded-3 p-3 p-lg-4 track_sec ${(trackedError?.isGuestRestricted || (isGuest && (!currentPageData || currentPageData.length === 0) && !trackedLoading && !trackedFetching)) ? '' : 'h-100 d-flex flex-column'}`}
                style={{ backgroundColor: "#21222D" }}
              >
                <div ref={rightHeaderRef}>
                  <h2 className="mb-4 d-flex align-items-center justify-content-between">
                    <span
                      style={{
                        fontFamily: "Rubik",
                        fontWeight: 600,
                        fontSize: "clamp(18px, 2vw, 22px)",
                        color: "rgba(255, 255, 255, 1)",
                      }}
                    >
                      Most Tracked Games
                    </span>
                    <div>
                      <button
                        ref={trackedFilterButtonRef}
                        type="button"
                        className="filter-trigger"
                        onClick={() => setShowFilters(true)}
                        aria-expanded={showFilters}
                        style={{
                          background: "transparent",
                          border: "1px solid rgba(255,255,255,0.12)",
                          color: "#fff",
                          borderRadius: 10,
                          padding: "7px 10px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 7,
                          cursor: "pointer",
                          fontWeight: 600,
                          fontSize: "14px",
                          lineHeight: 1,
                        }}
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          style={{ marginRight: 7 }}
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10 18h4v-2h-4v2zM4 6h16v2H4V6zm3 6h10v-2H7v2z"
                            fill="#fff"
                          />
                        </svg>
                        Filter
                        {trackedFiltersRaw &&
                          // `platform` is deliberately excluded here - it's the pill row
                          // rendered below (outside the Filters modal), not a modal filter,
                          // so selecting a platform shouldn't bump this badge's count.
                          Object.keys(trackedFiltersRaw).filter(k => k !== "platform").length > 0 && (
                            <span className="filter-badge">
                              {Object.entries(trackedFiltersRaw).reduce(
                                (acc, [key, v]) => {
                                  if (key === "platform") return acc
                                  if (v == null) return acc
                                  if (Array.isArray(v)) return acc + v.length
                                  if (
                                    typeof v === "string" ||
                                    typeof v === "number"
                                  )
                                    return acc + (String(v).trim() ? 1 : 0)
                                  return acc + 1
                                },
                                0,
                              )}
                            </span>
                          )}
                      </button>
                    </div>
                  </h2>

                  {/* Platform filter, moved up from inside the Filters modal so it's
                      visible at a glance — single-select (only one of Meta Quest /
                      Steam / Apple Vision active at a time, same as the Custom
                      Insights pages), rendered as pill chips to match those pages'
                      look. Negative top margin pulls it up against the title — the
                      `h2` above still carries Bootstrap's `mb-4` (24px) for the
                      Filter button's row height, which is too much space before
                      the pills. */}
                  <div style={{ marginTop: "-16px", marginBottom: "10px" }}>
                    <PlatformTabs
                      variant="pill"
                      showDivider={false}
                      selected={Array.isArray(trackedFiltersRaw?.platform) ? trackedFiltersRaw.platform : []}
                      onToggle={handleTrackedPlatformToggle}
                    />
                  </div>

                  <p
                    style={{
                      color: "rgba(255, 255, 255, 0.75)",
                      fontSize: "14px",
                      lineHeight: 1.4,
                      marginTop: "-6px",
                      marginBottom: "14px",
                    }}
                  >
                    Discover the most tracked apps of the week on KeyQuest —
                    updated every week based on user activity, search frequency,
                    and engagement trends.
                  </p>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginBottom: "12px",
                    }}
                  >
                    <div
                      style={{
                        color: "rgba(255,255,255,0.75)",
                        fontSize: "13px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Last updated:{" "}
                      {trackedLastUpdated
                        ? new Date(trackedLastUpdated).toLocaleString()
                        : "—"}
                    </div>
                  </div>

                  {/* Applied Filter Badges for Tracked Apps */}
                  {Object.keys(trackedFiltersRaw || {}).length > 0 && (
                    <div style={{ marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {Object.entries(trackedFiltersRaw).map(([key, value]) => {
                        // Platform has its own pill row above now — don't show it twice.
                        if (key === 'platform') return null;
                        if (!value || (Array.isArray(value) && value.length === 0)) return null;
                        const displayValue = Array.isArray(value) ? value.join(', ') : value;
                        return (
                          <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: 'linear-gradient(90deg, rgba(255,154,184,0.12), rgba(246,37,90,0.12))', color: '#F6255A', borderRadius: '6px', fontSize: '12px', fontWeight: 500 }}>
                            <span style={{ textTransform: 'capitalize', marginRight: 4 }}>{key.replace(/_/g, ' ')}:</span>
                            <span style={{ opacity: 0.9, color: '#F6255A' }}>{displayValue}</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleRemoveTrackedFilter(key);
                              }}
                              aria-label={`Remove filter ${key}`}
                              style={{
                                marginLeft: 4,
                                background: 'transparent',
                                border: 'none',
                                color: '#F6255A',
                                cursor: 'pointer',
                                fontSize: 12,
                                lineHeight: 0.9
                              }}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {showFilters && (
                    <FilterPanel
                      open={showFilters}
                      sections={filterSections.length > 0 ? filterSections : undefined}
                      initialValues={trackedFiltersRaw}
                      onClose={() => setShowFilters(false)}
                      anchorRef={trackedFilterButtonRef}
                      centerPosition={true}
                      // Platform is now surfaced as its own pill row next to the title
                      // (below), not duplicated inside this modal.
                      hidePlatformSection={true}
                      onApply={sel => {
                        trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_FILTER_APPLIED, {
                          section: "most_tracked",
                          filters: summarizeFilters(sel),
                        })
                        applyTrackedFilters(sel || {})
                        setShowFilters(false)
                      }}
                    />
                  )}
                </div>

                {/* Scrollable list area: height computed from left main minus header - Two column layout */}
                <div
                  ref={rightListRef}
                  style={{
                    flex: (trackedError?.isGuestRestricted || (isGuest && (!currentPageData || currentPageData.length === 0) && !trackedLoading && !trackedFetching)) ? 'none' : 1,
                    overflowY: (trackedError?.isGuestRestricted || (isGuest && (!currentPageData || currentPageData.length === 0) && !trackedLoading && !trackedFetching)) ? 'visible' : 'auto',
                    height: (trackedError?.isGuestRestricted || (isGuest && (!currentPageData || currentPageData.length === 0) && !trackedLoading && !trackedFetching)) ? 'auto' : (rightScrollHeight ? `${rightScrollHeight}px` : undefined),
                    paddingRight: 8,
                  }}
                >
                  {(trackedError?.isGuestRestricted || (isGuest && (!currentPageData || currentPageData.length === 0) && !trackedLoading && !trackedFetching)) ? (
                    <GuestRestrictedPlaceholder 
                      height="250px"
                      subMessage="Sign in to view the most tracked games and trending engagement metrics."
                    />
                  ) : (trackedLoading || trackedFetching) ? (
                    isMobile ? (
                      <>
                        {Array.from({ length: 4 }).map((_, i) => (
                          <Skeleton
                            key={i}
                            height={86}
                            style={{ width: "100%", marginBottom: 12 }}
                            borderRadius={10}
                            baseColor="#171821"
                            highlightColor="#2a2b37"
                            enableAnimation={true}
                          />
                        ))}
                      </>
                    ) : (
                      <SectionListSkeleton />
                    )
                  ) : currentPageData && currentPageData.length > 0 ? (
                    // Single column layout on mobile, two-column layout on larger screens
                    isMobile ? (
                      // Mobile: Single column layout
                      currentPageData.map((trackedApp, index) => (
                        <Link
                          key={`link-${trackedApp.item_id}-${index}`}
                          to={`/xr/app/${generateSlug(trackedApp.name || "")}?id=${trackedApp.item_id}`}
                          state={{
                            app_id: trackedApp.item_id,
                            data: trackedApp,
                            source_surface: "home_most_tracked",
                          }}
                          onClick={() => {
                            trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_APP_SELECTED, {
                              section: "most_tracked",
                              app_id: trackedApp.item_id,
                              app_name: trackedApp.name,
                            })
                          }}
                          style={{
                            textDecoration: "none",
                            display: "block",
                            marginBottom: 12,
                          }}
                        >
                          <TrackedGame
                            key={`mobile-${trackedApp.item_id}-${index}`}
                            trackedApp={trackedApp}
                          />
                        </Link>
                      ))
                    ) : (
                      // Desktop/Tablet: Single-column stacked layout (one app per row)
                      currentPageData.map((trackedApp, index) => (
                        <Link
                          key={`link-${trackedApp.item_id}-${index}`}
                          to={`/xr/app/${generateSlug(trackedApp.name || "")}?id=${trackedApp.item_id}`}
                          state={{
                            app_id: trackedApp.item_id,
                            data: trackedApp,
                            source_surface: "home_most_tracked",
                          }}
                          onClick={() => {
                            trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_APP_SELECTED, {
                              section: "most_tracked",
                              app_id: trackedApp.item_id,
                              app_name: trackedApp.name,
                            })
                          }}
                          style={{
                            textDecoration: "none",
                            display: "block",
                            marginBottom: 16,
                          }}
                        >
                          <TrackedGame
                            key={`desktop-${trackedApp.item_id}-${index}`}
                            trackedApp={trackedApp}
                          />
                        </Link>
                      ))
                    )
                  ) : (
                    <div
                      style={{ padding: 12, color: "rgba(255,255,255,0.8)" }}
                    >
                      No apps available
                    </div>
                  )}
                </div>

                {/* Pagination controls for the Most Tracked apps (right column) - keep outside the scroll area */}
                {!(trackedLoading || trackedFetching) && totalPages > 1 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 6,
                      marginTop: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        const next = Math.max(1, trackedPage - 1)
                        trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_PAGINATED, {
                          section: "most_tracked",
                          page: next,
                        })
                        setTrackedPage(next)
                      }}
                      disabled={trackedPage <= 1}
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(255,255,255,0.06)",
                        color:
                          trackedPage <= 1 ? "rgba(255,255,255,0.35)" : "#fff",
                        padding: "4px 8px",
                        borderRadius: 4,
                        fontSize: "12px",
                        cursor: trackedPage <= 1 ? "not-allowed" : "pointer",
                      }}
                    >
                      Prev
                    </button>

                    {getPageNumbers(trackedPage, totalPages).map(
                      (page, index) => {
                        if (page === "...") {
                          return (
                            <span
                              key={`right-ellipsis-${index}`}
                              style={{
                                minWidth: 28,
                                height: 28,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "rgba(255,255,255,0.8)",
                                fontWeight: 600,
                                fontSize: "12px",
                              }}
                            >
                              ...
                            </span>
                          )
                        }
                        return (
                          <button
                            key={`right-pg-${page}`}
                            type="button"
                            onClick={() => {
                              const next = Math.min(Math.max(1, page), totalPages)
                              trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_PAGINATED, {
                                section: "most_tracked",
                                page: next,
                              })
                              setTrackedPage(next)
                            }}
                            style={{
                              minWidth: 28,
                              height: 28,
                              borderRadius: 4,
                              border: "1px solid rgba(255,255,255,0.06)",
                              background:
                                trackedPage === page
                                  ? "#ff2f6d"
                                  : "transparent",
                              color:
                                trackedPage === page
                                  ? "#fff"
                                  : "rgba(255,255,255,0.8)",
                              fontWeight: 600,
                              fontSize: "12px",
                              cursor: "pointer",
                            }}
                          >
                            {page}
                          </button>
                        )
                      },
                    )}

                    <button
                      type="button"
                      onClick={() => {
                        const next = Math.min(totalPages, trackedPage + 1)
                        trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_PAGINATED, {
                          section: "most_tracked",
                          page: next,
                        })
                        setTrackedPage(next)
                      }}
                      disabled={trackedPage >= totalPages}
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(255,255,255,0.06)",
                        color:
                          trackedPage >= totalPages
                            ? "rgba(255,255,255,0.35)"
                            : "#fff",
                        padding: "4px 8px",
                        borderRadius: 4,
                        fontSize: "12px",
                        cursor:
                          trackedPage >= totalPages ? "not-allowed" : "pointer",
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Section 1.5: Explore More Tools */}
          <div
            className="mt-4 w-100"
            style={{
              background: "#21222D",
              border: "1px solid #353748",
              borderRadius: "8px",
              overflow: "visible",
            }}
          >
            <div
              className="rounded-3 p-3 p-lg-4"
              style={{ backgroundColor: "#21222D", overflow: "visible" }}
            >
              <h2 className="mb-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
                <span
                  style={{
                    fontFamily: "Rubik",
                    fontWeight: 600,
                    fontSize: "clamp(16px, 2vw, 22px)",
                    color: "rgba(255, 255, 255, 1)",
                  }}
                >
                  Explore More Tools
                </span>
              </h2>

              <p
                style={{
                  color: "rgba(255, 255, 255, 0.75)",
                  fontSize: "14px",
                  lineHeight: 1.4,
                  marginTop: "-6px",
                  marginBottom: "14px",
                }}
              >
                Dive deeper into your apps with AI-powered marketing insights, tracking, and cross-platform comparisons.
              </p>

              <Row className="gx-4 gy-4">
                <Col xs={12} md={6}>
                  <div
                    className="dashboard-tool-card"
                    style={{
                      background: "#171821",
                      border: "1px solid #353748",
                      borderRadius: "8px",
                      padding: "16px 18px",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "14px",
                    }}
                  >
                    <Link
                      to="/ai-cmo"
                      className="dashboard-tool-card-link"
                      onClick={() => {
                        trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_TOOL_SELECTED, {
                          tool_name: "ai_cmo",
                        })
                      }}
                      style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "14px" }}
                    >
                      <div
                        style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "10px",
                          background: "linear-gradient(135deg, rgba(255,154,184,0.15), rgba(246,37,90,0.15))",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <i className="uil-search" style={{ fontSize: "20px", color: "#F6255A" }}></i>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontFamily: "Rubik",
                            fontWeight: 600,
                            fontSize: "15px",
                            color: "rgba(255,255,255,1)",
                            marginBottom: "2px",
                          }}
                        >
                          AI CMO
                        </div>
                        <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "13px", lineHeight: 1.4 }}>
                          Your AI marketing agent for competitive analysis, ASO strategy, and multi-platform campaign insights.
                        </div>
                      </div>
                      <i className="uil-angle-right" style={{ fontSize: "18px", color: "rgba(255,255,255,0.4)", flexShrink: 0 }}></i>
                    </Link>

                    {(myTrackedAppsLoading || myTrackedAppsFetching) ? (
                      <div style={{ display: "flex", gap: "10px" }}>
                        {Array.from({ length: 4 }).map((_, idx) => (
                          <Skeleton key={idx} width={56} height={72} borderRadius={8} baseColor="#21222D" highlightColor="#2a2b37" enableAnimation={true} />
                        ))}
                      </div>
                    ) : myTrackedApps && myTrackedApps.length > 0 ? (
                      <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "12px" }}>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "rgba(255,255,255,0.5)",
                            marginBottom: "8px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Jump straight into a tracked app
                        </div>
                        <div style={{ display: "flex", gap: "10px", overflowX: "auto" }}>
                          {myTrackedApps.slice(0, 4).map((app, idx) => (
                            <Link
                              key={app.item_id || idx}
                              to={`/ai-cmo/${generateSlug(app.category || "apps") || "apps"}/${generateSlug(app.name || "")}?id=${app.item_id}`}
                              title={app.name}
                              onClick={() => {
                                trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_TOOL_SELECTED, {
                                  tool_name: "ai_cmo",
                                  app_id: app.item_id,
                                  app_name: app.name,
                                })
                              }}
                              className="dashboard-tool-app-thumb"
                              style={{
                                textDecoration: "none",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                width: "64px",
                                flexShrink: 0,
                              }}
                            >
                              <img
                                src={app.image_url || "https://via.placeholder.com/100"}
                                alt={app.name}
                                style={{
                                  width: "48px",
                                  height: "48px",
                                  borderRadius: "8px",
                                  objectFit: "cover",
                                  border: "1px solid #353748",
                                }}
                              />
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: "rgba(255,255,255,0.75)",
                                  marginTop: "4px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  width: "100%",
                                  textAlign: "center",
                                }}
                              >
                                {app.name}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div
                    className="dashboard-tool-card"
                    style={{
                      background: "#171821",
                      border: "1px solid #353748",
                      borderRadius: "8px",
                      padding: "16px 18px",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "14px",
                    }}
                  >
                    <Link
                      to="/social-media-comparison"
                      className="dashboard-tool-card-link"
                      onClick={() => {
                        trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_TOOL_SELECTED, {
                          tool_name: "social_comparison",
                        })
                      }}
                      style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "14px" }}
                    >
                      <div
                        style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "10px",
                          background: "linear-gradient(135deg, rgba(255,154,184,0.15), rgba(246,37,90,0.15))",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <i className="uil-share-alt" style={{ fontSize: "20px", color: "#F6255A" }}></i>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontFamily: "Rubik",
                            fontWeight: 600,
                            fontSize: "15px",
                            color: "rgba(255,255,255,1)",
                            marginBottom: "2px",
                          }}
                        >
                          Social Comparison
                        </div>
                        <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "13px", lineHeight: 1.4 }}>
                          Compare your app's social presence and engagement against competitors across platforms.
                        </div>
                      </div>
                      <i className="uil-angle-right" style={{ fontSize: "18px", color: "rgba(255,255,255,0.4)", flexShrink: 0 }}></i>
                    </Link>

                    {(myTrackedAppsLoading || myTrackedAppsFetching) ? (
                      <div style={{ display: "flex", gap: "10px" }}>
                        {Array.from({ length: 4 }).map((_, idx) => (
                          <Skeleton key={idx} width={56} height={72} borderRadius={8} baseColor="#21222D" highlightColor="#2a2b37" enableAnimation={true} />
                        ))}
                      </div>
                    ) : myTrackedApps && myTrackedApps.length > 0 ? (
                      <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "12px" }}>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "rgba(255,255,255,0.5)",
                            marginBottom: "8px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Jump straight into a tracked app
                        </div>
                        <div style={{ display: "flex", gap: "10px", overflowX: "auto" }}>
                          {myTrackedApps.slice(0, 4).map((app, idx) => (
                            <Link
                              key={app.item_id || idx}
                              to={`/social-media-comparison?item_id=${app.item_id}&name=${encodeURIComponent(app.name || "")}&image_url=${encodeURIComponent(app.image_url || "")}`}
                              title={app.name}
                              onClick={() => {
                                trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_TOOL_SELECTED, {
                                  tool_name: "social_comparison",
                                  app_id: app.item_id,
                                  app_name: app.name,
                                })
                              }}
                              className="dashboard-tool-app-thumb"
                              style={{
                                textDecoration: "none",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                width: "64px",
                                flexShrink: 0,
                              }}
                            >
                              <img
                                src={app.image_url || "https://via.placeholder.com/100"}
                                alt={app.name}
                                style={{
                                  width: "48px",
                                  height: "48px",
                                  borderRadius: "8px",
                                  objectFit: "cover",
                                  border: "1px solid #353748",
                                }}
                              />
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: "rgba(255,255,255,0.75)",
                                  marginTop: "4px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  width: "100%",
                                  textAlign: "center",
                                }}
                              >
                                {app.name}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col xs={12} md={6}>
                  <div
                    className="dashboard-tool-card"
                    style={{
                      background: "#171821",
                      border: "1px solid #353748",
                      borderRadius: "8px",
                      padding: "16px 18px",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "14px",
                    }}
                  >
                    <Link
                      to="/app-tracking"
                      className="dashboard-tool-card-link"
                      onClick={() => {
                        trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_TOOL_SELECTED, {
                          tool_name: "ai_report",
                        })
                      }}
                      style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "14px" }}
                    >
                      <div
                        style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "10px",
                          background: "linear-gradient(135deg, rgba(255,154,184,0.15), rgba(246,37,90,0.15))",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <i className="uil-analytics" style={{ fontSize: "20px", color: "#F6255A" }}></i>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontFamily: "Rubik",
                            fontWeight: 600,
                            fontSize: "15px",
                            color: "rgba(255,255,255,1)",
                            marginBottom: "2px",
                          }}
                        >
                          AI Report
                        </div>
                        <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "13px", lineHeight: 1.4 }}>
                          A condensed AI-generated report on any app's performance, positioning, and opportunities.
                        </div>
                      </div>
                      <i className="uil-angle-right" style={{ fontSize: "18px", color: "rgba(255,255,255,0.4)", flexShrink: 0 }}></i>
                    </Link>

                    {(myTrackedAppsLoading || myTrackedAppsFetching) ? (
                      <div style={{ display: "flex", gap: "10px" }}>
                        {Array.from({ length: 4 }).map((_, idx) => (
                          <Skeleton key={idx} width={56} height={72} borderRadius={8} baseColor="#21222D" highlightColor="#2a2b37" enableAnimation={true} />
                        ))}
                      </div>
                    ) : myTrackedApps && myTrackedApps.length > 0 ? (
                      <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "12px" }}>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "rgba(255,255,255,0.5)",
                            marginBottom: "8px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Jump straight into a tracked app
                        </div>
                        <div style={{ display: "flex", gap: "10px", overflowX: "auto" }}>
                          {myTrackedApps.slice(0, 4).map((app, idx) => (
                            <Link
                              key={app.item_id || idx}
                              to={`/xr/app/${generateSlug(app.name || "")}?id=${app.item_id}&open=ai-report`}
                              title={app.name}
                              onClick={() => {
                                trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_TOOL_SELECTED, {
                                  tool_name: "ai_report",
                                  app_id: app.item_id,
                                  app_name: app.name,
                                })
                              }}
                              className="dashboard-tool-app-thumb"
                              style={{
                                textDecoration: "none",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                width: "64px",
                                flexShrink: 0,
                              }}
                            >
                              <img
                                src={app.image_url || "https://via.placeholder.com/100"}
                                alt={app.name}
                                style={{
                                  width: "48px",
                                  height: "48px",
                                  borderRadius: "8px",
                                  objectFit: "cover",
                                  border: "1px solid #353748",
                                }}
                              />
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: "rgba(255,255,255,0.75)",
                                  marginTop: "4px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  width: "100%",
                                  textAlign: "center",
                                }}
                              >
                                {app.name}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col xs={12} md={6}>
                  <div
                    className="dashboard-tool-card"
                    style={{
                      background: "#171821",
                      border: "1px solid #353748",
                      borderRadius: "8px",
                      padding: "16px 18px",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "14px",
                    }}
                  >
                    <Link
                      to="/app-tracking"
                      className="dashboard-tool-card-link"
                      onClick={() => {
                        trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_TOOL_SELECTED, {
                          tool_name: "forecasting",
                        })
                      }}
                      style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "14px" }}
                    >
                      <div
                        style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "10px",
                          background: "linear-gradient(135deg, rgba(255,154,184,0.15), rgba(246,37,90,0.15))",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <i className="uil-chart-line" style={{ fontSize: "20px", color: "#F6255A" }}></i>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontFamily: "Rubik",
                            fontWeight: 600,
                            fontSize: "15px",
                            color: "rgba(255,255,255,1)",
                            marginBottom: "2px",
                          }}
                        >
                          Forecasting
                        </div>
                        <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "13px", lineHeight: 1.4 }}>
                          AI-powered forecasts for downloads, revenue, and growth trends for any app you track.
                        </div>
                      </div>
                      <i className="uil-angle-right" style={{ fontSize: "18px", color: "rgba(255,255,255,0.4)", flexShrink: 0 }}></i>
                    </Link>

                    {(myTrackedAppsLoading || myTrackedAppsFetching) ? (
                      <div style={{ display: "flex", gap: "10px" }}>
                        {Array.from({ length: 4 }).map((_, idx) => (
                          <Skeleton key={idx} width={56} height={72} borderRadius={8} baseColor="#21222D" highlightColor="#2a2b37" enableAnimation={true} />
                        ))}
                      </div>
                    ) : myTrackedApps && myTrackedApps.length > 0 ? (
                      <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "12px" }}>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "rgba(255,255,255,0.5)",
                            marginBottom: "8px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Jump straight into a tracked app
                        </div>
                        <div style={{ display: "flex", gap: "10px", overflowX: "auto" }}>
                          {myTrackedApps.slice(0, 4).map((app, idx) => (
                            <Link
                              key={app.item_id || idx}
                              to={`/xr/app/${generateSlug(app.name || "")}?id=${app.item_id}&open=forecasting`}
                              title={app.name}
                              onClick={() => {
                                trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_TOOL_SELECTED, {
                                  tool_name: "forecasting",
                                  app_id: app.item_id,
                                  app_name: app.name,
                                })
                              }}
                              className="dashboard-tool-app-thumb"
                              style={{
                                textDecoration: "none",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                width: "64px",
                                flexShrink: 0,
                              }}
                            >
                              <img
                                src={app.image_url || "https://via.placeholder.com/100"}
                                alt={app.name}
                                style={{
                                  width: "48px",
                                  height: "48px",
                                  borderRadius: "8px",
                                  objectFit: "cover",
                                  border: "1px solid #353748",
                                }}
                              />
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: "rgba(255,255,255,0.75)",
                                  marginTop: "4px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  width: "100%",
                                  textAlign: "center",
                                }}
                              >
                                {app.name}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </Col>
              </Row>
            </div>
          </div>

          {/* Section 2: Top Trending Games */}
          <div
            className="mt-4 w-100"
            style={{
              background: "#21222D",
              border: "1px solid #353748",
              borderRadius: "8px",
              overflow: "visible",
            }}
          >
            <div
              className="rounded-3 p-3 p-lg-4"
              style={{ backgroundColor: "#21222D", overflow: "visible" }}
            >
              <h2
                className="mb-4 d-flex align-items-center justify-content-between flex-wrap gap-2"
                style={{
                  visibility: "visible",
                  display: "flex",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <span
                  style={{
                    fontFamily: "Rubik",
                    fontWeight: 600,
                    fontSize: "clamp(16px, 2vw, 22px)",
                    color: "rgba(255, 255, 255, 1)",
                    visibility: "visible",
                    display: "block",
                  }}
                >
                  Top Performer Apps
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button
                      ref={topPerfFilterButtonRef}
                      type="button"
                      className="filter-trigger"
                      onClick={() => setShowTopPerfFilters(true)}
                      aria-expanded={showTopPerfFilters}
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(255,255,255,0.12)",
                        color: "#fff",
                        borderRadius: 10,
                        padding: "7px 10px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 7,
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: "14px",
                        lineHeight: 1,
                      }}
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        style={{ marginRight: 7 }}
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10 18h4v-2h-4v2zM4 6h16v2H4V6zm3 6h10v-2H7v2z"
                          fill="#fff"
                        />
                      </svg>
                      Filter
                      {topPerfFiltersRaw &&
                        // `platform` is deliberately excluded here - it's the pill row
                        // rendered below (outside the Filters modal), not a modal filter,
                        // so selecting a platform shouldn't bump this badge's count.
                        Object.keys(topPerfFiltersRaw).filter(k => k !== "platform").length > 0 && (
                          <span className="filter-badge">
                            {Object.entries(topPerfFiltersRaw).reduce(
                              (acc, [key, v]) => {
                                if (key === "platform") return acc
                                if (v == null) return acc
                                if (Array.isArray(v)) return acc + v.length
                                if (
                                  typeof v === "string" ||
                                  typeof v === "number"
                                )
                                  return acc + (String(v).trim() ? 1 : 0)
                                return acc + 1
                              },
                              0,
                            )}
                          </span>
                        )}
                    </button>
                  </div>
                </div>
              </h2>

              {/* Platform filter, moved up from inside the Filters modal so it's
                  visible at a glance — single-select (only one of Meta Quest /
                  Steam / Apple Vision active at a time), rendered as pill chips,
                  same pattern as the Most Tracked Games section above. */}
              <div style={{ marginTop: "-16px", marginBottom: "10px" }}>
                <PlatformTabs
                  variant="pill"
                  showDivider={false}
                  selected={Array.isArray(topPerfFiltersRaw?.platform) ? topPerfFiltersRaw.platform : []}
                  onToggle={handleTopPerfPlatformToggle}
                />
              </div>

              <p
                style={{
                  color: "rgba(255, 255, 255, 0.75)",
                  fontSize: "14px",
                  lineHeight: 1.4,
                  marginTop: "-6px",
                  marginBottom: "14px",
                }}
              >
                Top performing apps of the week, ranked according to KeyQuest’s
                App Score — a proprietary metric that blends Meta Store data
                with performance indicators such as user feedback, engagement,
                and technical stability.
              </p>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: "12px",
                }}
              >
                <div
                  style={{
                    color: "rgba(255,255,255,0.75)",
                    fontSize: "13px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Last updated:{" "}
                  {topPerfLastUpdated
                    ? new Date(topPerfLastUpdated).toLocaleString()
                    : "—"}
                </div>
              </div>

              {/* Applied Filter Badges for Top Performers */}
              {Object.keys(topPerfFiltersRaw || {}).length > 0 && (
                <div style={{ marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {Object.entries(topPerfFiltersRaw).map(([key, value]) => {
                    // Platform has its own pill row above now — don't show it twice.
                    if (key === 'platform') return null;
                    if (!value || (Array.isArray(value) && value.length === 0)) return null;
                    const displayValue = Array.isArray(value) ? value.join(', ') : value;
                    return (
                      <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: 'linear-gradient(90deg, rgba(255,154,184,0.12), rgba(246,37,90,0.12))', color: '#F6255A', borderRadius: '6px', fontSize: '12px', fontWeight: 500 }}>
                        <span style={{ textTransform: 'capitalize', marginRight: 4 }}>{key.replace(/_/g, ' ')}:</span>
                        <span style={{ opacity: 0.9, color: '#F6255A' }}>{displayValue}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleRemoveTopPerfFilter(key);
                          }}
                          aria-label={`Remove filter ${key}`}
                          style={{
                            marginLeft: 4,
                            background: 'transparent',
                            border: 'none',
                            color: '#F6255A',
                            cursor: 'pointer',
                            fontSize: 12,
                            lineHeight: 0.9
                          }}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              {showTopPerfFilters && (
                <FilterPanel
                  open={showTopPerfFilters}
                  sections={filterSections.length > 0 ? filterSections : undefined}
                  initialValues={topPerfFiltersRaw}
                  onClose={() => setShowTopPerfFilters(false)}
                  anchorRef={topPerfFilterButtonRef}
                  centerPosition={true}
                  // Platform is now surfaced as its own pill row next to the title
                  // (above), not duplicated inside this modal.
                  hidePlatformSection={true}
                  onApply={sel => {
                    trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_FILTER_APPLIED, {
                      section: "top_performer",
                      filters: summarizeFilters(sel),
                    })
                    // Clear any cached sessionStorage entries for topPerformance to avoid stale cached responses
                    Object.keys(sessionStorage).forEach(key => {
                      if (key && key.startsWith("topPerformance"))
                        sessionStorage.removeItem(key)
                    })

                    applyTopPerfFilters(sel || {})
                    setShowTopPerfFilters(false)
                  }}
                />
              )}

              <Row className="trending_slider">
                {topPerfLoading ? (
                  <div style={{ paddingLeft: isMobile ? "0" : `${cardGap}px`, paddingRight: isMobile ? "0" : `${cardGapHalf}px` }}>
                    <div className="d-flex gap-3" style={{ gap: isMobile ? "12px" : `${cardGap}px` }}>
                      {Array.from({ length: topPerfPerPage }).map((_, idx) => {
                        const cardDims = isMobile
                          ? {
                            maxWidth: "280px",
                            minWidth: "260px",
                            height: "350px",
                            imageHeight: "280px",
                            imageWidth: "280px",
                          }
                          : windowWidth < 1200
                            ? {
                              maxWidth: "95px",
                              minWidth: "90px",
                              height: "115px",
                              imageHeight: "55px",
                              imageWidth: "55px",
                            }
                            : {
                              maxWidth: "100px",
                              minWidth: "95px",
                              height: "120px",
                              imageHeight: "57px",
                              imageWidth: "57px",
                            }
                        return (
                          <div key={idx} style={{ minWidth: cardDims.minWidth, maxWidth: cardDims.maxWidth }}>
                            <Skeleton
                              height={cardDims.height}
                              borderRadius={8}
                              baseColor="#171821"
                              highlightColor="#2a2b37"
                              enableAnimation={true}
                            />
                            {isMobile && (
                              <Skeleton
                                width="70%"
                                height={16}
                                style={{ marginTop: "12px" }}
                                baseColor="#171821"
                                highlightColor="#2a2b37"
                                enableAnimation={true}
                              />
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ) : (topPerfError?.isGuestRestricted || (isGuest && (!topPerformanceApps || topPerformanceApps.length === 0) && !topPerfLoading)) ? (
                  <div style={{ paddingLeft: isMobile ? "0" : `${cardGap}px`, paddingRight: isMobile ? "0" : `${cardGapHalf}px`, width: "100%" }}>
                    <GuestRestrictedPlaceholder 
                      height="350px"
                      subMessage="Sign in to unlock top performing app scores and detailed performance analytics."
                    />
                  </div>
                ) : topPerformanceApps && topPerformanceApps.length > 0 ? (
                  <div style={{ paddingLeft: isMobile ? "0" : `${cardGap}px`, paddingRight: isMobile ? "0" : `${cardGapHalf}px` }}>
                    <CarouselComponent
                      games={topCurrent}
                      title="Top Performer Apps"
                      defaultOpenGraph={"app_score"}
                      disableLink={true}
                      onCardClick={handleTopPerfClick}
                      selectedAppId={selectedTopAppId}
                      cardDimsOverride={{
                        mobile: {
                          maxWidth: '280px',
                          minWidth: '260px',
                          height: '350px',
                          imageHeight: '280px',
                          imageWidth: '280px',
                        }
                      }}
                    // slidesToShowOverride={isMobile ? undefined : 9}
                    />

                    {/* Pagination for Top Performer Apps (match visual style) */}
                    {!topPerfLoading && topTotalPages > 1 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 6,
                          marginTop: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            const next = Math.max(1, topPageSafe - 1)
                            trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_PAGINATED, {
                              section: "top_performer",
                              page: next,
                            })
                            setTopPerfPage(next)
                          }}
                          disabled={topPageSafe <= 1}
                          style={{
                            background: "transparent",
                            border: "1px solid rgba(255,255,255,0.06)",
                            color:
                              topPageSafe <= 1
                                ? "rgba(255,255,255,0.35)"
                                : "#fff",
                            padding: "4px 8px",
                            borderRadius: 4,
                            fontSize: "12px",
                            cursor:
                              topPageSafe <= 1 ? "not-allowed" : "pointer",
                          }}
                        >
                          Prev
                        </button>

                        {getPageNumbers(topPageSafe, topTotalPages).map(
                          (page, index) => {
                            if (page === "...") {
                              return (
                                <span
                                  key={`top-ellipsis-${index}`}
                                  style={{
                                    minWidth: 28,
                                    height: 28,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "rgba(255,255,255,0.8)",
                                    fontWeight: 600,
                                    fontSize: "12px",
                                  }}
                                >
                                  ...
                                </span>
                              )
                            }
                            return (
                              <button
                                key={`top-pg-${page}`}
                                type="button"
                                onClick={() => {
                                  const next = Math.min(Math.max(1, page), topTotalPages)
                                  trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_PAGINATED, {
                                    section: "top_performer",
                                    page: next,
                                  })
                                  setTopPerfPage(next)
                                }}
                                style={{
                                  minWidth: 28,
                                  height: 28,
                                  borderRadius: 4,
                                  border: "1px solid rgba(255,255,255,0.06)",
                                  background:
                                    topPageSafe === page
                                      ? "#ff2f6d"
                                      : "transparent",
                                  color:
                                    topPageSafe === page
                                      ? "#fff"
                                      : "rgba(255,255,255,0.8)",
                                  fontWeight: 600,
                                  fontSize: "12px",
                                  cursor: "pointer",
                                }}
                              >
                                {page}
                              </button>
                            )
                          },
                        )}

                        <button
                          type="button"
                          onClick={() => {
                            const next = Math.min(topTotalPages, topPageSafe + 1)
                            trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_PAGINATED, {
                              section: "top_performer",
                              page: next,
                            })
                            setTopPerfPage(next)
                          }}
                          disabled={topPageSafe >= topTotalPages}
                          style={{
                            background: "transparent",
                            border: "1px solid rgba(255,255,255,0.06)",
                            color:
                              topPageSafe >= topTotalPages
                                ? "rgba(255,255,255,0.35)"
                                : "#fff",
                            padding: "4px 8px",
                            borderRadius: 4,
                            fontSize: "12px",
                            cursor:
                              topPageSafe >= topTotalPages
                                ? "not-allowed"
                                : "pointer",
                          }}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ padding: 18, color: "rgba(255,255,255,0.8)" }}>
                    No apps available
                  </div>
                )}
              </Row>

              {/* Inline App Score graph panel (appears when a top performer is clicked) */}
              {selectedTopAppId &&
                (() => {
                  // Use the deduplicated `fullTop` list to locate the selected app so
                  // it matches what's shown in the carousel/pagination.
                  const selectedApp =
                    fullTop?.find(a => a.item_id === selectedTopAppId) || null
                  return (
                    <div
                      style={{
                        marginTop: 18,
                        padding: 12,
                        borderRadius: 8,
                        background: "#18181f",
                        border: "1px solid #2f2f3a",
                      }}
                    >
                      <div
                        className="d-flex gap-3 mb-3"
                        style={{
                          flexDirection: isMobile ? "column" : "row",
                          alignItems: isMobile ? "flex-start" : "center",
                          justifyContent: isMobile
                            ? "flex-start"
                            : "space-between",
                          gap: isMobile ? 12 : undefined,
                        }}
                      >
                        {/* Left side: Image and Text */}
                        <div
                          style={{
                            display: "flex",
                            gap: 12,
                            alignItems: "center",
                            width: isMobile ? "100%" : "auto",
                            justifyContent: isMobile
                              ? "space-between"
                              : "flex-start",
                            flex: isMobile ? "none" : 1,
                          }}
                        >
                          <div
                            style={{
                              width: isMobile ? 56 : 72,
                              height: isMobile ? 56 : 72,
                              borderRadius: 8,
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={selectedApp?.image_url || ""}
                              alt="app"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </div>

                          {!isMobile && (
                            <div style={{ flex: 1, minWidth: 0 }}>
                              {/* Show a small badge above the app name indicating the data window (7 days) */}
                              {(() => {
                                const daysCount = Array.isArray(
                                  selectedTopAppData,
                                )
                                  ? selectedTopAppData.length
                                  : 0
                                const label =
                                  daysCount >= 7
                                    ? "7 days data"
                                    : `${daysCount} day${daysCount === 1 ? "" : "s"}`
                                return (
                                  <div style={{ marginBottom: 6 }}>
                                    <span
                                      style={{
                                        fontSize: 12,
                                        background: "#2b2b35",
                                        color: "rgba(255,255,255,0.85)",
                                        padding: "4px 8px",
                                        borderRadius: 999,
                                        fontWeight: 600,
                                      }}
                                    >
                                      {label}
                                    </span>
                                  </div>
                                )
                              })()}

                              <h3 style={{ margin: 0, color: "#fff", fontSize: '18px', fontWeight: 500 }}>
                                {selectedApp?.name || "App"}
                              </h3>
                              <div
                                style={{
                                  color: "rgba(255,255,255,0.7)",
                                  fontSize: 12,
                                }}
                              >
                                App Score trend
                              </div>
                            </div>
                          )}

                          {/* Mobile: Button inside the left container */}
                          {isMobile && (
                            <div
                              style={{
                                flexShrink: 0,
                                alignSelf: "flex-start",
                              }}
                            >
                              <Link
                                to={`/xr/app/${generateSlug(selectedApp?.name || "")}?id=${selectedTopAppId}`}
                                state={{
                                  app_id: selectedTopAppId,
                                  data: selectedApp,
                                  openGraph: "app_score",
                                  source_surface: "home_top_performer",
                                }}
                                onClick={() => {
                                  trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_APP_SELECTED, {
                                    section: "top_performer",
                                    app_id: selectedTopAppId,
                                    app_name: selectedApp?.name,
                                  })
                                }}
                                style={{ textDecoration: "none" }}
                              >
                                <button
                                  type="button"
                                  style={{
                                    background: "transparent",
                                    border: "1px solid #ff4f7a",
                                    color: "#fff",
                                    borderRadius: 8,
                                    padding: "4px 8px",
                                    cursor: "pointer",
                                    fontWeight: 500,
                                    fontSize: "11px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  View App Details
                                </button>
                              </Link>
                            </div>
                          )}
                        </div>

                        {/* Right side: Button (web view only) */}
                        {!isMobile && (
                          <div
                            style={{
                              flexShrink: 0,
                              alignSelf: "center",
                            }}
                          >
                            <Link
                              to={`/xr/app/${generateSlug(selectedApp?.name || "")}?id=${selectedTopAppId}`}
                              state={{
                                app_id: selectedTopAppId,
                                data: selectedApp,
                                openGraph: "app_score",
                                source_surface: "home_top_performer",
                              }}
                              onClick={() => {
                                trackEvent(ANALYTICS_EVENTS.FEATURE.DASHBOARD_APP_SELECTED, {
                                  section: "top_performer",
                                  app_id: selectedTopAppId,
                                  app_name: selectedApp?.name,
                                })
                              }}
                              style={{ textDecoration: "none" }}
                            >
                              <button
                                type="button"
                                style={{
                                  background: "transparent",
                                  border: "2px solid #ff4f7a",
                                  color: "#fff",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                  cursor: "pointer",
                                  fontWeight: 500,
                                  fontSize: "13px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                View App Details
                              </button>
                            </Link>
                          </div>
                        )}
                      </div>

                      {/* Mobile: Text content below image/button row */}
                      {isMobile && (
                        <div style={{ width: "100%" }}>
                          {/* Show a small badge above the app name indicating the data window (7 days) */}
                          {(() => {
                            const daysCount = Array.isArray(selectedTopAppData)
                              ? selectedTopAppData.length
                              : 0
                            const label =
                              daysCount >= 7
                                ? "7 days data"
                                : `${daysCount} day${daysCount === 1 ? "" : "s"}`
                            return (
                              <div style={{ marginBottom: 6 }}>
                                <span
                                  style={{
                                    fontSize: 10,
                                    background: "#2b2b35",
                                    color: "rgba(255,255,255,0.85)",
                                    padding: "3px 6px",
                                    borderRadius: 999,
                                    fontWeight: 600,
                                  }}
                                >
                                  {label}
                                </span>
                              </div>
                            )
                          })()}

                          <h3
                            style={{
                              margin: 0,
                              color: "#fff",
                              fontSize: "14px",
                              fontWeight: 500,
                              lineHeight: "1.3",
                            }}
                          >
                            {selectedApp?.name || "App"}
                          </h3>
                          <div
                            style={{
                              color: "rgba(255,255,255,0.7)",
                              fontSize: 11,
                            }}
                          >
                            App Score trend
                          </div>
                        </div>
                      )}

                      <div style={{ minHeight: 220 }}>
                        {selectedTopAppLoading ? (
                          <Skeleton
                            height={220}
                            borderRadius={8}
                            baseColor="#171821"
                            highlightColor="#2a2b37"
                            enableAnimation={true}
                          />
                        ) : selectedTopAppData &&
                          selectedTopAppData.length > 0 ? (
                          <KTRLineChart
                            values="asr"
                            jsonData={selectedTopAppData}
                            startDate={null}
                            endDate={null}
                          />
                        ) : (
                          <div
                            style={{
                              color: "rgba(255,255,255,0.7)",
                              padding: 18,
                            }}
                          >
                            No graph data available.
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })()}
            </div>
          </div>

          {/* New Releases section removed per request */}
        </Container>
      </div>
    </React.Fragment>
  )
}

export default OptimizedDashboard
